import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Loading from '../Loading';
import TripCardModal from './TripCardModal';
import EstimateModal from './EstimateModal';
import DlvModal from './DlvModal';
import { ReceptionStatus, ReceptionType, PaymentMethod, ProofType, ReceptionItemKind, InboundMethod, FlowType } from 'enums/reception.enum';
import { ProductCategory, ProductUseType } from 'enums/product.enum';
import receptionService from 'services/reception.service';
import { ReceptionDTO } from 'models/reception';
import { TripCardDTO } from 'models/trip-card';

import productService from 'services/product.service';
import tripFeeService from 'services/trip-fee.service';
import authService from 'services/auth.service';
import { ReceptionItemDTO, RepairOption } from 'models/reception-item';
import moment from 'moment';
import { float } from 'html2canvas/dist/types/css/property-descriptors/float';

// 상태별 한글 레이블
const STATUS_LABELS: Record<ReceptionStatus, string> = {
    "CHECK": '중요체크',
    'RECEIVED': '접수중',
    'INBOUND_SCHEDULED': '입고예정',
    'OUTBOUND_READY': '출고대기',
    'TRIP_READY': '출장대기',
    'REPAIRING': '수리중',
    'ARREARS': '미수',
    'PAID': '결제완료',
    'DONE': '완료',
    'CONSULTED': '상담완료',
};

const TYPE_LABELS: Record<ReceptionType, string> = {
    'GENERAL': '일반',
    'EXCHANGE': '맞교환',
    'TRIP': '출장',
    'COMP_SALE': '보상판매',
    'VISIT': '방문',
    'SALE': '판매',
    'REPAIR_COMP': '리퍼보상',
    'NEW_EXCHANGE': '신상맞교환',
    // 'COMP_SALE_TRIP': '보상판매출장',
};

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
    'BANK': '계좌이체',
    'CARD': '카드',
    'CASH': '현금',
    'FREE': '무상',
    'ARREARS': '미수',
    'OTHER': '기타',
};

const PROOF_LABELS: Record<ProofType, string> = {
    'CASH_RECEIPT': '현금영수증',
    'RECEIPT': '영수발행',
    'INVOICE': '청구발행',
    'BUSINESS': '사업자증빙',
    'NONE': '미발행',
    'NARABIL': '나라빌'
};

// 상품 옵션 인터페이스
interface ProductOption {
    id: number;
    productCode: string;
    productName: string;
    useType: ProductUseType;
    category: ProductCategory;
    price: number;
    cost: number;
}

// ===== ReceptionModal 수정 부분 1: 타입 정의 및 인터페이스 =====

// 기존 TripFeeOption 인터페이스 수정
interface TripFeeOption {
    type: 'province' | 'city';  // 타입 추가
    name: string;               // province 또는 city 이름
    tripFee?: number;          // 도: 고객 청구 금액
    tripCost?: number;         // 지역: 출장자 지불 비용
}

// 수리 카운트 인터페이스 (UI 상태 관리용)
interface RepairCount {
    productId: number;
    productName: string;
    quantity: number;
    unitPrice: number;
    unitCost: number;
}

interface RepairCounts {
    [productName: string]: RepairCount;
}

interface EstimateItem {
    productName: string;
    quantity: number;
    unitPrice: number;
    unitCost: number;
    totalPrice: number;
    memo: string;
    isDiscount?: boolean;
    sourceItemIndex?: number;  // 원본 item의 인덱스
    sourceItemId?: number;     // 원본 item의 ID
    sourceType?: 'ITEM_GROUP' | 'STANDALONE' | 'DISCOUNT';  // 항목 유형
    parentName?: string;       // 부모 이름 (그룹화용)
    category?: ProductCategory;
    kind?: ReceptionItemKind;
}

interface DeliveryCompanyOption {
    name: string;
    isCustom: boolean;
}

type Props = {
    show: boolean;
    title?: string;
    loading?: boolean;
    initial?: ReceptionDTO | null;
    onSave: (reception: ReceptionDTO, close: boolean) => Promise<void> | void;
    onClose: () => void;
    onOpenEstimate?: (reception: ReceptionDTO) => void;
};

const defaultReception: ReceptionDTO = {
    receivedDate: moment().format('YYYY-MM-DD'),
    estimateDate: null,
    status: ReceptionStatus.RECEIVED,
    type: ReceptionType.GENERAL,
    blacklist: false,
    estimateAmount: 0,
    repairCost: 0,
    tripFeeAmount: 0,
    tripFee: 0,
    logisticsFee: 0,
    profit: 0,
    items: []
};

const ReceptionModal: React.FC<Props> = ({
    show,
    title = '접수 등록/수정',
    loading = false,
    initial,
    onSave,
    onClose,
    onOpenEstimate
}) => {

    const [form, setForm] = useState<ReceptionDTO>(defaultReception);
    const [showRepairModal, setShowRepairModal] = useState(false);
    const [repairItemIndex, setRepairItemIndex] = useState<number | null>(null);
    const [repairCounts, setRepairCounts] = useState<RepairCounts>({});

    // 출장카드 모달 상태
    const [showTripCardModal, setShowTripCardModal] = useState(false);
    const [tripCard, setTripCard] = useState<TripCardDTO | null>(null);

    // 견적서 모달 상태
    const [showEstimateModal, setShowEstimateModal] = useState(false);

    // 마스터 데이터
    const [products, setProducts] = useState<ProductOption[]>([]);
    const [loadingData, setLoadingData] = useState(false);

    // 보일러명 자동완성 관련 상태
    const [boilerNameSuggestions, setBoilerNameSuggestions] = useState<{ [itemIndex: number]: ProductOption[] }>({});
    const [showBoilerSuggestions, setShowBoilerSuggestions] = useState<{ [itemIndex: number]: boolean }>({});
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<{ [itemIndex: number]: number }>({});

    // 지역 자동완성 관련 상태
    const [tripFees, setTripFees] = useState<TripFeeOption[]>([]);
    const [provinceSuggestions, setProvinceSuggestions] = useState<TripFeeOption[]>([]);
    const [showProvinceSuggestions, setShowProvinceSuggestions] = useState(false);
    const [citySuggestions, setCitySuggestions] = useState<TripFeeOption[]>([]);
    const [showCitySuggestions, setShowCitySuggestions] = useState(false);

    // 운송사 관련 상태
    const [deliveryCompanyOptions, setDeliveryCompanyOptions] = useState<DeliveryCompanyOption[]>([]);
    const [customDlvCompany, setCustomDlvCompany] = useState<{ [itemIndex: number]: string }>({});
    const [useCustomDlvCompany, setUseCustomDlvCompany] = useState<{ [itemIndex: number]: boolean }>({});

    // 택배 모달
    const [showDlvModal, setShowDlvModal] = useState(false);
    const [dlvModalType, setDlvModalType] = useState<'inbound' | 'outbound'>('inbound');

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // 권한체크
    const [hasFinancePermission, setHasFinancePermission] = useState(false);

    const formTypeRef = useRef(form.type);

    useEffect(() => {
        formTypeRef.current = form.type;
    }, [form.type]);

    // 권한 체크 useEffect 추가
    useEffect(() => {
        const token = authService.getToken();
        const roles = token?.roles || [];

        // master 또는 admin 권한이 있는 경우에만 금액 정보 표시
        const hasPermission = roles.includes('master') || roles.includes('admin');
        setHasFinancePermission(hasPermission);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadDeliveryCompanyOptions = () => {
        setDeliveryCompanyOptions([
            { name: '로젠', isCustom: false },
            { name: '경동', isCustom: false },
            { name: '대신', isCustom: false },
            { name: '직접입력', isCustom: true }
        ]);
    };

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                // 수리 모달이 열려있으면 메인 모달은 닫지 않음
                if (!showRepairModal && !showTripCardModal && !showEstimateModal) {
                    handleClose();
                }
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [show, showRepairModal, showTripCardModal, showEstimateModal, onClose]);

    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showRepairModal) {
                closeRepairModal();
            }
        };

        if (showRepairModal) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [showRepairModal]);


    // 데이터 로딩
    useEffect(() => {
        if (show) {
            loadMasterData();
        }
    }, [show]);

    // 폼 초기화
    useEffect(() => {
        if (show) {

            console.log("initial", initial);

            const defultItem = new ReceptionItemDTO();
            defultItem.kind = ReceptionItemKind.BOILER;
            if (!initial?.items?.length) defaultReception.items = [defultItem];
            setForm({
                ...defaultReception,
                ...(initial || {}),
                items: initial?.items ? [...initial.items] : [...defaultReception.items]
            });
            setRepairCounts({});

            // 보일러명 자동완성 초기화
            setBoilerNameSuggestions({});
            setShowBoilerSuggestions({});

            // 지역 자동완성 초기화
            setCitySuggestions([]);
            setShowCitySuggestions(false);

            // 운송사 옵션 로드
            loadDeliveryCompanyOptions();

            // 운송사 상태 초기화
            if (initial?.items) {
                const customCompanies: { [key: number]: string } = {};
                const useCustom: { [key: number]: boolean } = {};

                initial.items.forEach((item, idx) => {
                    if (item.dlvCompany) {
                        const predefinedCompanies = ['CJ대한통운', '롯데택배', '한진택배', '우체국택배', '로젠택배'];
                        if (!predefinedCompanies.includes(item.dlvCompany)) {
                            useCustom[idx] = true;
                            customCompanies[idx] = item.dlvCompany;
                        }
                    }
                });

                setUseCustomDlvCompany(useCustom);
                setCustomDlvCompany(customCompanies);
            } else {
                setUseCustomDlvCompany({});
                setCustomDlvCompany({});
            }

            if (initial?.tripCard?.image && initial.tripCard.image.name) initial.tripCard.image._preview = initial.tripCard.image.name;

            // 출장카드 폼 초기화
            if (initial?.tripCard) {
                setTripCard({
                    id: initial.tripCard.id,
                    tripDate: initial.tripCard.tripDate,
                    agent: initial.tripCard.agent,
                    content: initial.tripCard.content,
                    image: initial.tripCard.image
                });
            } else {
                setTripCard(null);
            }
        }
    }, [initial, show]);

    const loadMasterData = async () => {
        try {
            setLoadingData(true);

            const productData = await productService.getActiveProducts();

            setProducts(productData.map(p => ({
                id: p.id!,
                productCode: p.productCode || '',
                productName: p.productName || '',
                useType: p.useType as ProductUseType,
                category: p.category as ProductCategory,
                price: p.price || 0,
                cost: p.costPrice || 0,
            })));

            // 출장비 데이터 로딩 - 타입별로 분리하여 저장
            const tripFeeData = await tripFeeService.list({ page: 1, rows: 9999 });
            setTripFees(tripFeeData.content.map(fee => ({
                type: fee.type,
                name: fee.name,
                tripFee: fee.tripFee || undefined,
                tripCost: fee.tripCost || undefined,
            })));

        } catch (error) {
            console.error('마스터 데이터 로딩 실패:', error);
        } finally {
            setLoadingData(false);
        }
    };

    // 폼 변경 핸들러
    const onChange = useCallback((name: keyof ReceptionDTO, value: any) => {
        setForm(prev => ({ ...prev, [name]: value }));
    }, []);

    // 아이템 변경 핸들러
    const changeItem = useCallback((idx: number, patch: Partial<ReceptionItemDTO>) => {
        setForm(prev => {
            const items = [...(prev.items || [])];
            items[idx] = { ...items[idx], ...patch };
            return { ...prev, items };
        });
    }, []);

    // 아이템 요약 생성 (options 배열 기반) - 개선된 방식
    const getItemSummary = useCallback((item: ReceptionItemDTO): string => {
        if (!item.options || item.options.length === 0) {
            return '';
        }

        return item.options
            .filter(option => option.quantity > 0)
            .map(option => option.quantity === 1 ? option.productName : `${option.productName}×${option.quantity}`)
            .join(', ');
    }, []);

    // 아이템 추가/제거
    const addItem = useCallback((kind: ReceptionItemKind = ReceptionItemKind.BOILER) => {
        setForm(prev => ({
            ...prev,
            items: [
                ...(prev.items || []),
                {
                    id: 0,
                    kind,
                    totalAmount: 0,
                    totalCost: 0,
                    totalQuantity: 1,
                    inboundMethod: null,
                    flow: null,
                    dlvFee: null,
                    options: [], // JSON 옵션 배열
                    repairDate: null,
                    summary: '' // 계산된 요약
                } as ReceptionItemDTO
            ]
        }));
    }, []);

    const removeItem = useCallback((idx: number) => {
        setForm(prev => ({
            ...prev,
            items: (prev.items || []).filter((_, i) => i !== idx)
        }));
        // 해당 아이템의 자동완성 상태도 제거
        setBoilerNameSuggestions(prev => {
            const newSuggestions = { ...prev };
            delete newSuggestions[idx];
            return newSuggestions;
        });
        setShowBoilerSuggestions(prev => {
            const newShow = { ...prev };
            delete newShow[idx];
            return newShow;
        });
    }, []);

    // 상품 선택 시 자동 입력
    const selectProduct = useCallback((itemIndex: number, productId: number) => {
        const product = products.find(p => p.id === productId);
        if (product) {
            changeItem(itemIndex, {
                modelName: product.productName,
                totalAmount: product.price,
                totalCost: product.cost,
            });
        }
    }, [products, changeItem]);

    // 운송사 변경 핸들러
    const onDlvCompanyChange = useCallback((itemIndex: number, value: string) => {
        if (value === '직접입력') {
            setUseCustomDlvCompany(prev => ({ ...prev, [itemIndex]: true }));
            // 기존 커스텀 값이 있으면 유지, 없으면 빈 문자열
            if (!customDlvCompany[itemIndex]) {
                setCustomDlvCompany(prev => ({ ...prev, [itemIndex]: '' }));
            }
        } else {
            setUseCustomDlvCompany(prev => ({ ...prev, [itemIndex]: false }));
            changeItem(itemIndex, { dlvCompany: value });
            // 커스텀 값 초기화
            setCustomDlvCompany(prev => {
                const newCustom = { ...prev };
                delete newCustom[itemIndex];
                return newCustom;
            });
        }
    }, [customDlvCompany, changeItem]);

    // 커스텀 운송사명 변경 핸들러
    const onCustomDlvCompanyChange = useCallback((itemIndex: number, value: string) => {
        setCustomDlvCompany(prev => ({ ...prev, [itemIndex]: value }));
        changeItem(itemIndex, { dlvCompany: value });
    }, [changeItem]);

    // 합계 계산 (options 기반) - 개선된 방식
    const totals = useMemo(() => {
        const items = form.items || [];

        let itemsAmount = 0;  // 수리/판매 항목 금액
        let repairCost = 0;
        let logisticsFee = 0;
        let discount = 0;
        const allOptions: RepairOption[] = [];

        items.forEach(item => {
            itemsAmount += item.totalAmount || 0;
            repairCost += item.totalCost || 0;
            discount += item.kind === ReceptionItemKind.DISCOUNT ? item.totalAmount : 0;
            logisticsFee += item.dlvFee || 0;
            allOptions.push(...item.options || []);
        });

        // productCode별로 quantity 합치기
        const mergedOptions = allOptions.reduce((acc, opt) => {
            const existing = acc.find(item => item.productName === opt.productName);
            if (existing) {
                existing.quantity += opt.quantity;
            } else {
                acc.push({ ...opt });
            }
            return acc;
        }, [] as typeof allOptions);

        const allSummaries = mergedOptions
            .filter(opt => opt.quantity > 0)
            .map(opt => opt.quantity === 1 ? opt.productName : `${opt.productName}×${opt.quantity}`)
            .join(', ');

        // 출장비 처리
        const tripFee = form.tripFee || 0;              // 고객 청구 출장비 (도) - 수익
        const tripFeeAmount = form.tripFeeAmount || 0;  // 출장자 지불 비용 (지역) - 비용

        // ✅ 견적금액 = 수리/판매 금액 + 고객 청구 출장비
        const estimateAmount = itemsAmount + tripFee;

        // ✅ 이익 = 견적금액 - (수리원가 + 출장 비용 + 물류비)
        const profit = estimateAmount - (repairCost + tripFeeAmount + logisticsFee);

        const totalSummary = allSummaries;

        return {
            itemsAmount,      // 수리/판매 항목만의 금액 (새로 추가)
            estimateAmount,   // 최종 견적금액 (수리비 + 고객 청구 출장비)
            repairCost,
            logisticsFee,
            tripFeeAmount,    // 출장 비용 (출장자 지불)
            tripFee,          // 출장비 (고객 청구)
            discount,
            profit,
            totalSummary,
        };
    }, [form.items, form.tripFeeAmount, form.tripFee]);

    // 수리 모달 관련
    const openRepairModal = useCallback((itemIndex: number) => {
        setRepairItemIndex(itemIndex);
        setShowRepairModal(true);

        // 현재 아이템의 options를 파싱하여 카운트 설정
        const item = form.items?.[itemIndex];
        if (item?.options) {
            const counts = parseOptionsToRepairCounts(item.options);
            setRepairCounts(counts);
        } else {
            setRepairCounts({});
        }
    }, [form.items]);

    const closeRepairModal = useCallback(() => {
        setShowRepairModal(false);
        setRepairItemIndex(null);
        setRepairCounts({});
    }, []);

    // options 배열을 RepairCounts로 변환 - 개선된 방식
    const parseOptionsToRepairCounts = useCallback((options: RepairOption[]): RepairCounts => {
        const counts: RepairCounts = {};

        options.forEach(option => {
            if (option.productName) {
                const product = products.find(p => p.productName === option.productName);
                counts[option.productName] = {
                    productId: product?.id || -1,
                    productName: option.productName,
                    quantity: option.quantity || 1,
                    unitPrice: option.unitPrice || 0,
                    unitCost: option.unitCost || 0
                };
            }
        });

        return counts;
    }, [products]);

    // RepairCounts를 options 배열로 변환
    const convertRepairCountsToOptions = useCallback((): RepairOption[] => {
        // 현재 편집 중인 아이템의 kind 가져오기
        const currentItemKind = repairItemIndex !== null
            ? form.items?.[repairItemIndex]?.kind
            : ReceptionItemKind.BOILER;

        return Object.values(repairCounts)
            .filter(item => item.quantity > 0)
            .map(item => {
                const product = products.find(p => p.id === item.productId);
                return {
                    productCode: product?.productCode || '',
                    productName: item.productName,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    unitCost: item.unitCost,
                    category: product?.category,
                    kind: currentItemKind  // ⭐ 추가
                };
            });
    }, [repairCounts, products, repairItemIndex, form.items]);

    // 수리내역 비용 계산
    const calculateRepairCosts = () => {
        let totalPrice = 0;
        let totalCost = 0;

        // 무상 여부 확인
        const isFree = repairCounts['무상']?.quantity > 0;

        Object.values(repairCounts).forEach(item => {
            // '무상' 항목은 비용 계산에서 제외
            if (item.productName !== '무상') {
                totalCost += item.quantity * item.unitCost;
                // 무상이 체크되어 있으면 totalPrice에 합산하지 않음
                if (!isFree) {
                    totalPrice += item.quantity * item.unitPrice;
                }
            }
        });

        // 무상이면 totalPrice는 0으로 반환
        return { totalPrice: isFree ? 0 : totalPrice, totalCost };
    };

    // 수리 카운트 업데이트
    const updateRepairCount = useCallback((productName: string, count: number, product?: ProductOption) => {
        setRepairCounts(prev => {
            const newCounts = { ...prev };

            if (count <= 0) {
                delete newCounts[productName];
            } else {
                if (newCounts[productName]) {
                    newCounts[productName] = {
                        ...newCounts[productName],
                        quantity: count
                    };
                } else if (product) {
                    newCounts[productName] = {
                        productId: product.id,
                        productName: product.productName,
                        quantity: count,
                        unitPrice: product.price,
                        unitCost: product.cost
                    };
                } else if (productName === '무상') {
                    newCounts[productName] = {
                        productId: -1,
                        productName: '무상',
                        quantity: count,
                        unitPrice: 0,
                        unitCost: 0
                    };
                }
            }

            return newCounts;
        });
    }, []);

    // 수리내역 저장 - 개선된 방식 (JSON 옵션 사용)
    const saveRepairDetails = useCallback(() => {
        if (repairItemIndex !== null) {
            const options = convertRepairCountsToOptions();
            const { totalPrice, totalCost } = calculateRepairCosts();

            // 요약 문자열 생성
            const summary = options
                .filter(option => option.quantity > 0)
                .map(option => option.quantity === 1 ? option.productName : `${option.productName}×${option.quantity}`)
                .join(', ');

            changeItem(repairItemIndex, {
                options,
                totalAmount: totalPrice,
                totalCost: totalCost,
                summary
            });

            changeItem(repairItemIndex, {
                repairDate: moment().format("YYYY-MM-DD")
            });
        }
        closeRepairModal();
    }, [repairItemIndex, convertRepairCountsToOptions, calculateRepairCosts, changeItem, closeRepairModal]);

    // 출장카드 모달 관련
    const openTripCardModal = useCallback(() => {
        const currentAddress = `${form.province || ''} ${form.city || ''} ${form.address || ''}`.trim();

        setTripCard({
            id: tripCard?.id,
            tripDate: tripCard?.tripDate || '',
            agent: tripCard?.agent || '',
            content: tripCard?.content || '',
            image: tripCard?.image
        });

        setShowTripCardModal(true);
    }, [form, tripCard]);

    const closeTripCardModal = useCallback(() => {
        setShowTripCardModal(false);
    }, []);

    const saveTripCard = useCallback((data: ReceptionDTO | null) => {
        data.status = ReceptionStatus.TRIP_READY;
        data.type = ReceptionType.TRIP;
        if (data.tripCard?.tripDate) data.status = ReceptionStatus.INBOUND_SCHEDULED
        if (data) {
            setForm(data);
        }
        setShowTripCardModal(false);
    }, []);

    const openEstimateModal = useCallback(() => {
        setShowEstimateModal(true);
    }, []);

    const closeEstimateModal = useCallback((estimateItems: EstimateItem[], updatedReception?: Partial<ReceptionDTO>) => {
        if (!estimateItems || estimateItems.length === 0) {
            setShowEstimateModal(false);
            return;
        }

        if (updatedReception) {
            if (!form?.estimateDate && updatedReception.estimateDate) {
                setForm(prev => ({
                    ...prev,
                    // status: ReceptionStatus.ARREARS,
                    paymentMethod: PaymentMethod.ARREARS,
                    ...updatedReception
                }));
            } else {
                setForm(prev => ({
                    ...prev,
                    ...updatedReception
                }));
            }
        }

        const originalItems = [...form.items];
        const newItems: ReceptionItemDTO[] = [];

        // 1. 항목들을 유형별로 분류
        const itemUpdates = new Map<number, EstimateItem[]>();
        const newStandaloneItems: EstimateItem[] = [];
        const newDiscountItems: EstimateItem[] = [];

        estimateItems.forEach(estimateItem => {
            if (!estimateItem.productName && !estimateItem.parentName || (estimateItem.productName || estimateItem.parentName).trim() === '') return;

            switch (estimateItem.sourceType) {
                case 'DISCOUNT':
                    newDiscountItems.push(estimateItem);
                    break;

                case 'ITEM_GROUP':
                    if (estimateItem.sourceItemIndex !== undefined) {
                        if (!itemUpdates.has(estimateItem.sourceItemIndex)) {
                            itemUpdates.set(estimateItem.sourceItemIndex, []);
                        }
                        itemUpdates.get(estimateItem.sourceItemIndex)!.push(estimateItem);
                    }
                    break;

                case 'STANDALONE':
                default:
                    if (estimateItem.sourceItemIndex === undefined) {
                        newStandaloneItems.push(estimateItem);
                    } else {
                        if (!itemUpdates.has(estimateItem.sourceItemIndex)) {
                            itemUpdates.set(estimateItem.sourceItemIndex, []);
                        }
                        itemUpdates.get(estimateItem.sourceItemIndex)!.push(estimateItem);
                    }
                    break;
            }
        });

        // 2. 기존 items 처리 - 🔥 삭제 처리 포함
        originalItems.forEach((item, index) => {
            if (itemUpdates.has(index)) {
                const updateItems = itemUpdates.get(index)!;

                if (updateItems.length === 1 && updateItems[0].sourceType === 'STANDALONE') {
                    const est = updateItems[0];
                    newItems.push({
                        ...item,
                        modelName: est.productName,
                        totalAmount: est.totalPrice || 0,
                        totalQuantity: est.quantity || 1,
                        options: [{
                            productCode: '',
                            productName: est.productName,
                            quantity: est.quantity || 1,
                            unitPrice: est.unitPrice || 0,
                            unitCost: est.unitCost || 0,
                            category: est.category,
                            kind: est.kind || item.kind  // ⭐ 추가
                        }],
                        summary: est.productName
                    });
                } else {
                    // 다중 옵션 항목
                    const options = updateItems.map(est => {
                        const optionName = est.productName.includes(' - ')
                            ? est.productName.split(' - ').pop()?.trim() || est.productName
                            : est.productName;

                        return {
                            productCode: '',
                            productName: optionName,
                            quantity: est.quantity || 1,
                            unitPrice: est.unitPrice || 0,
                            unitCost: est.unitCost || 0,
                            category: est.category,
                            kind: est.kind || item.kind  // ⭐ 추가
                        };
                    });

                    const totalAmount = options.reduce((sum, opt) => sum + (opt.quantity * opt.unitPrice), 0);
                    const totalCost = options.reduce((sum, opt) => sum + (opt.quantity * opt.unitCost), 0);
                    const summary = options
                        .map(opt => opt.quantity === 1 ? opt.productName : `${opt.productName}×${opt.quantity}`)
                        .join(', ');

                    newItems.push({
                        ...item,
                        options,
                        totalAmount,
                        totalCost,
                        summary
                    });
                }
            } else {
                // 견적서에 없는 item 처리
                const hadEstimateItems = item.options && item.options.length > 0;

                if (hadEstimateItems) {
                    newItems.push({
                        ...item,
                        options: [],
                        totalAmount: 0,
                        totalCost: 0,
                        summary: ''
                    });
                } else {
                    newItems.push({ ...item });
                }
            }
        });

        // 3. 완전히 새로운 독립 항목들 추가
        newStandaloneItems.forEach(item => {
            newItems.push({
                id: 0,
                kind: item.kind || ReceptionItemKind.SALE,
                modelName: item.productName,
                totalAmount: item.totalPrice || 0,
                totalCost: 0,
                totalQuantity: item.quantity || 1,
                inboundMethod: null,
                flow: null,
                dlvFee: null,
                options: [{
                    productCode: '',
                    productName: item.productName,
                    quantity: item.quantity || 1,
                    unitPrice: item.unitPrice || 0,
                    unitCost: 0,
                    category: item.category,
                    kind: item.kind
                }],
                repairDate: null,
                summary: item.productName
            } as ReceptionItemDTO);
        });

        // 4. 할인 항목들도 독립적인 item으로 추가
        newDiscountItems.forEach(discountItem => {
            newItems.push({
                id: 0,
                kind: ReceptionItemKind.DISCOUNT,
                modelName: discountItem.productName || discountItem.parentName,
                totalAmount: discountItem.totalPrice || 0,
                totalCost: 0,
                totalQuantity: discountItem.quantity || 1,
                inboundMethod: null,
                flow: null,
                dlvFee: null,
                options: [{
                    productCode: '',
                    productName: discountItem.productName || discountItem.parentName,
                    quantity: discountItem.quantity || 1,
                    unitPrice: discountItem.unitPrice || 0,
                    unitCost: 0,
                    category: discountItem.category,
                    kind: ReceptionItemKind.DISCOUNT
                }],
                repairDate: null,
                summary: discountItem.productName || discountItem.parentName
            } as ReceptionItemDTO);
        });

        // 5. 🔥 할인 전용 kind가 없는 경우를 위한 빈 items 정리
        const finalItems = newItems.filter(item => {
            // 빈 options와 의미없는 데이터를 가진 item 제거
            if ((!item.options || item.options.length === 0) &&
                (!item.modelName || item.modelName.trim() === '') &&
                (item.totalAmount === 0)) {
                return false;
            }
            return true;
        });

        // 최소 하나의 item은 유지
        if (finalItems.length === 0) {
            finalItems.push({
                id: 0,
                kind: ReceptionItemKind.BOILER,
                totalAmount: 0,
                totalCost: 0,
                totalQuantity: 1,
                inboundMethod: null,
                flow: null,
                dlvFee: null,
                options: [],
                repairDate: null,
                summary: ''
            } as ReceptionItemDTO);
        }

        // 6. 전체 견적 금액 재계산
        const totalEstimateAmount = estimateItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);

        setForm(prev => ({
            ...prev,
            items: finalItems,
            estimateAmount: totalEstimateAmount,
        }));

        setShowEstimateModal(false);
    }, [form]);

    const handleClose = useCallback(() => {
        if (!confirm('저장하시겠습니까?')) {
            onClose();
        } else {
            handleSave();
        }
    }, [form, totals, onClose]);

    // 저장 처리
    const handleSave = useCallback(async () => {
        // 기본 유효성 검사
        if (!form.phone1?.trim()) {
            alert('연락처를 입력해주세요.');
            return;
        }

        // ⭐ 출장비 유효성 검사 추가 (선택사항)
        if (form.province && !form.tripFee) {
            if (!confirm('도가 입력되었지만 출장비(고객 청구)가 0원입니다. 계속하시겠습니까?')) {
                return;
            }
        }

        if (form.city && !form.tripFeeAmount) {
            if (!confirm('지역이 입력되었지만 출장 비용(출장자 지불)이 0원입니다. 계속하시겠습니까?')) {
                return;
            }
        }

        // 합계 반영한 최종 폼
        const finalForm: ReceptionDTO = {
            ...form,
            estimateAmount: totals.estimateAmount,
            repairCost: totals.repairCost,
            logisticsFee: totals.logisticsFee,
            tripFeeAmount: totals.tripFeeAmount,  // 출장 비용 (출장자)
            tripFee: totals.tripFee,        // 출장비 (고객)
            profit: totals.profit,
        };

        await onSave(finalForm, true);
    }, [form, totals, onSave]);

    // 폼 초기화 함수
    const resetForm = useCallback(() => {
        // 기존 폼에서 유지할 데이터 추출
        const preservedData = {
            province: form.province,
            city: form.city,
            address: form.address,
            customerName: form.customerName,
            phone1: form.phone1,
            phone2: form.phone2,
            phone3: form.phone3,
            proofType: form.proofType,
        };

        // 첫 번째 아이템의 보일러 정보 유지
        let defaultItems: ReceptionItemDTO[] = [];

        form.items.forEach(item => {
            if (item.kind === ReceptionItemKind.BOILER) {
                const preservedItemData = {
                    modelName: item.modelName,
                    manufactureMonth: item.manufactureMonth,
                    note: item.note,
                };

                const newDefaultItem: ReceptionItemDTO = {
                    id: 0,
                    kind: ReceptionItemKind.BOILER,
                    totalAmount: 0,
                    totalCost: 0,
                    totalQuantity: 1,
                    inboundMethod: null,
                    flow: null,
                    dlvFee: null,
                    options: [],
                    repairDate: null,
                    summary: '',
                    // 보일러 정보 유지
                    ...preservedItemData
                } as ReceptionItemDTO;
                defaultItems.push(newDefaultItem);
            }
        });

        const newForm = {
            ...defaultReception,
            receivedDate: moment().format('YYYY-MM-DD'),
            status: ReceptionStatus.RECEIVED,
            type: ReceptionType.GENERAL,
            // 주소 정보 및 고객 정보 유지
            ...preservedData,
            items: defaultItems
        };

        setForm(newForm);
        setRepairCounts({});
        setTripCard(null);
        setBoilerNameSuggestions({});
        setShowBoilerSuggestions({});

        // 지역 자동완성은 유지 (도시 정보가 있을 경우)
        if (preservedData.city) {
            const filteredCities = tripFees.filter(fee =>
                fee.name.toLowerCase().includes(preservedData.city.toLowerCase())
            ).slice(0, 10);
            setCitySuggestions(filteredCities);
        } else {
            setCitySuggestions([]);
        }
        setShowCitySuggestions(false);
    }, [form.province, form.city, form.address, form.customerName, form.phone1, form.phone2, form.phone3, form.items, tripFees]);

    // 저장 후 새접수 처리 함수
    const handleSaveAndNew = useCallback(async () => {
        if (!form.phone1?.trim()) {
            alert('연락처를 입력해주세요.');
            return;
        }

        try {
            // 합계 반영한 최종 폼
            const finalForm: ReceptionDTO = {
                ...form,
                estimateAmount: totals.estimateAmount,
                repairCost: totals.repairCost,
                logisticsFee: totals.logisticsFee,
                tripFeeAmount: totals.tripFeeAmount,  // 출장 비용 (출장자)
                tripFee: totals.tripFee,        // 출장비 (고객)
                profit: totals.profit,
            };

            await onSave(finalForm, false);
            resetForm();
        } catch (error) {
            console.error('저장 실패:', error);
        }
    }, [form, totals, onSave, resetForm]);

    const handleDelete = useCallback(async () => {
        if (!confirm('삭제하시겠습니까?')) return;
        try {
            await receptionService.remove(form.id);
            alert('삭제되었습니다.');
            window.location.reload();
        } catch (error) {
            console.error('접수 삭제 실패:', error);
            alert('삭제에 실패했습니다.');
        }
    }, [form.id]);

    const getModalTitleAndProducts = useCallback((itemKind: ReceptionItemKind) => {
        const isBoiler = itemKind === 'BOILER';

        return {
            modalTitle: isBoiler ? '수리내역' : '판매내역',
            iconClass: isBoiler ? 'fas fa-tools' : 'fas fa-shopping-cart',
            generalProducts: products?.filter(p =>
                p.category === 'PART' &&
                p.useType === (isBoiler ? 'AS' : 'SALE')
            ) || [],
            heavyProducts: products?.filter(p =>
                p.category === 'HEAVY_PART' &&
                p.useType === (isBoiler ? 'AS' : 'SALE')
            ) || [],
            sectionTitles: {
                general: isBoiler ? '수리내역(일반)' : '판매내역(일반)',
                heavy: isBoiler ? '수리내역(대용량)' : '판매내역(대용량)',
                etc: isBoiler ? '수리내역(기타)' : '판매내역(기타)'
            }
        };
    }, [products]);

    // 보일러명 검색 및 자동완성 핸들러
    const handleBoilerNameChange = useCallback((itemIndex: number, value: string) => {
        // 아이템 모델명 업데이트
        changeItem(itemIndex, { modelName: value });

        if (value.trim() === '') {
            setBoilerNameSuggestions(prev => ({ ...prev, [itemIndex]: [] }));
            setShowBoilerSuggestions(prev => ({ ...prev, [itemIndex]: false }));
            setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: -1 }));
            return;
        }

        // 현재 아이템의 kind에 따라 useType 결정
        const currentItem = form.items?.[itemIndex];
        const targetUseType = currentItem?.kind === 'BOILER' ? 'AS' : 'SALE';

        // 보일러 제품 필터링
        const filteredBoilers = products
            .filter(p =>
                p.category === 'BOILER' &&
                p.useType === targetUseType &&
                p.productName.toLowerCase().includes(value.toLowerCase())
            )
            .slice(0, 10); // 최대 10개까지만 표시

        setBoilerNameSuggestions(prev => ({ ...prev, [itemIndex]: filteredBoilers }));
        setShowBoilerSuggestions(prev => ({ ...prev, [itemIndex]: filteredBoilers.length > 0 }));
        setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: -1 })); // 초기화
    }, [products, changeItem, form.items]);

    // 보일러명 선택 핸들러
    const selectBoilerName = useCallback((itemIndex: number, product: ProductOption) => {
        const currentItem = form.items?.[itemIndex];

        if (currentItem?.kind === 'SALE') {
            const boilerOption: RepairOption = {
                productCode: product.productCode,
                productName: product.productName,
                quantity: 1,
                unitPrice: product.price,
                unitCost: product.cost,
                category: product.category,
                kind: ReceptionItemKind.SALE
            };

            const existingOptions = currentItem.options || [];
            const nonBoilerOptions = existingOptions.filter(opt =>
                opt.category !== 'BOILER'
            );

            const newOptions = [boilerOption, ...nonBoilerOptions];
            const totalAmount = newOptions.reduce((sum, opt) => sum + (opt.quantity * opt.unitPrice), 0);
            const totalCost = newOptions.reduce((sum, opt) => sum + (opt.quantity * opt.unitCost), 0);
            const summary = newOptions
                .filter(opt => opt.quantity > 0)
                .map(opt => opt.quantity === 1 ? opt.productName : `${opt.productName}×${opt.quantity}`)
                .join(', ');

            changeItem(itemIndex, {
                modelName: product.productName,
                options: newOptions,
                totalAmount,
                totalCost,
                summary
            });
        } else {
            changeItem(itemIndex, { modelName: product.productName });
        }

        setShowBoilerSuggestions(prev => ({ ...prev, [itemIndex]: false }));
        setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: -1 }));
    }, [changeItem, form.items, products]);

    const handleBoilerNameKeyDown = useCallback((itemIndex: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        const suggestions = boilerNameSuggestions[itemIndex] || [];
        const currentIndex = selectedSuggestionIndex[itemIndex] ?? -1;

        if (!showBoilerSuggestions[itemIndex] || suggestions.length === 0) {
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                const nextIndex = currentIndex < suggestions.length - 1 ? currentIndex + 1 : 0;
                setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: nextIndex }));

                // 스크롤 이동 추가
                setTimeout(() => {
                    const suggestionElement = document.querySelector(
                        `[data-suggestion-index="${itemIndex}-${nextIndex}"]`
                    );
                    suggestionElement?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest'
                    });
                }, 0);
                break;

            case 'ArrowUp':
                e.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : suggestions.length - 1;
                setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: prevIndex }));

                // 스크롤 이동 추가
                setTimeout(() => {
                    const suggestionElement = document.querySelector(
                        `[data-suggestion-index="${itemIndex}-${prevIndex}"]`
                    );
                    suggestionElement?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest'
                    });
                }, 0);
                break;

            case 'Enter':
                e.preventDefault();
                if (currentIndex >= 0 && currentIndex < suggestions.length) {
                    selectBoilerName(itemIndex, suggestions[currentIndex]);
                }
                break;

            case 'Escape':
                e.preventDefault();
                setShowBoilerSuggestions(prev => ({ ...prev, [itemIndex]: false }));
                setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: -1 }));
                break;
        }
    }, [boilerNameSuggestions, selectedSuggestionIndex, showBoilerSuggestions, selectBoilerName]);

    // 보일러명 입력 필드 포커스 아웃 핸들러
    const handleBoilerNameBlur = useCallback((itemIndex: number) => {
        setTimeout(() => {
            setShowBoilerSuggestions(prev => ({ ...prev, [itemIndex]: false }));
            setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: -1 }));
        }, 150);
    }, []);

    // 보일러명 입력 필드 포커스 핸들러
    const handleBoilerNameFocus = useCallback((itemIndex: number) => {
        const currentValue = form.items?.[itemIndex]?.modelName || '';
        if (currentValue && boilerNameSuggestions[itemIndex]?.length > 0) {
            setShowBoilerSuggestions(prev => ({ ...prev, [itemIndex]: true }));
            setSelectedSuggestionIndex(prev => ({ ...prev, [itemIndex]: -1 }));
        }
    }, [form.items, boilerNameSuggestions]);

    // ===== 도(Province) 검색 및 자동완성 핸들러 =====
    const handleProvinceChange = useCallback((value: string) => {
        onChange('province', value);

        // const isTripType = formTypeRef.current === ReceptionType.TRIP
        //     || formTypeRef.current === ReceptionType.COMP_SALE_TRIP;

        const isTripType = formTypeRef.current === ReceptionType.TRIP;

        if (isTripType) {
            onChange('tripFee', 0);
        }

        if (value.trim() === '') {
            setProvinceSuggestions([]);
            setShowProvinceSuggestions(false);
            return;
        }

        const filteredProvinces = tripFees
            .filter(fee =>
                fee.type === 'province' &&
                fee.name.toLowerCase().includes(value.toLowerCase())
            )
            .slice(0, 10);

        setProvinceSuggestions(filteredProvinces);
        // setShowProvinceSuggestions(filteredProvinces.length > 0); // 드롭다운 노출 비활성화

        if (isTripType) {
            const exactMatch = tripFees.find(fee =>
                fee.type === 'province' &&
                fee.name.toLowerCase() === value.toLowerCase()
            );
            if (exactMatch?.tripFee) {
                onChange('tripFee', exactMatch.tripFee);
            }
        }
    }, [tripFees, onChange]); // form.type 제거, ref 사용이므로 dependency 불필요

    // ===== 도 선택 핸들러 =====
    const selectProvince = useCallback((fee: TripFeeOption) => {
        onChange('province', fee.name);

        // 도의 출장비(고객 청구) 자동 입력
        if (fee.tripFee) {
            onChange('tripFee', fee.tripFee);
        }

        setShowProvinceSuggestions(false);
    }, [onChange]);

    // ===== 도 입력 필드 포커스/블러 핸들러 =====
    const handleProvinceFocus = useCallback(() => {
        const currentValue = form.province || '';
        if (currentValue && provinceSuggestions.length > 0) {
            setShowProvinceSuggestions(true);
        }
    }, [form.province, provinceSuggestions]);

    const handleProvinceBlur = useCallback(() => {
        setTimeout(() => {
            setShowProvinceSuggestions(false);
        }, 150);
    }, []);

    // ===== 지역(City) 검색 및 자동완성 핸들러 =====
    const handleCityChange = useCallback((value: string) => {
        onChange('city', value);

        // const isTripType = formTypeRef.current === ReceptionType.TRIP
        //     || formTypeRef.current === ReceptionType.COMP_SALE_TRIP;

        const isTripType = formTypeRef.current === ReceptionType.TRIP;

        if (isTripType) {
            onChange('tripFeeAmount', 0);
        }

        if (value.trim() === '') {
            setCitySuggestions([]);
            setShowCitySuggestions(false);
            return;
        }

        const filteredCities = tripFees
            .filter(fee =>
                fee.type === 'city' &&
                fee.name.toLowerCase().includes(value.toLowerCase())
            )
            .slice(0, 10);

        setCitySuggestions(filteredCities);
        // setShowCitySuggestions(filteredCities.length > 0); // 드롭다운 노출 비활성화

        if (isTripType) {
            const exactMatch = tripFees.find(fee =>
                fee.type === 'city' &&
                fee.name.toLowerCase() === value.toLowerCase()
            );
            if (exactMatch?.tripCost) {
                onChange('tripFeeAmount', exactMatch.tripCost);
            }
        }
    }, [tripFees, onChange]);

    // ===== 지역 선택 핸들러 =====
    const selectCity = useCallback((fee: TripFeeOption) => {
        onChange('city', fee.name);

        // 지역의 출장 비용(출장자 지불) 자동 입력
        if (fee.tripCost) {
            onChange('tripFeeAmount', fee.tripCost);
        }

        setShowCitySuggestions(false);
    }, [onChange]);

    // ===== 지역 입력 필드 포커스/블러 핸들러 =====
    const handleCityFocus = useCallback(() => {
        const currentValue = form.city || '';
        if (currentValue && citySuggestions.length > 0) {
            setShowCitySuggestions(true);
        }
    }, [form.city, citySuggestions]);

    const handleCityBlur = useCallback(() => {
        setTimeout(() => {
            setShowCitySuggestions(false);
        }, 150);
    }, []);

    const openDlvModal = useCallback((type: 'inbound' | 'outbound') => {
        setDlvModalType(type);
        setShowDlvModal(true);
    }, []);

    const handleDlvModalSave = useCallback(async (reception: ReceptionDTO, close: boolean) => {
        try {
            // reception 저장
            // await onSave(reception, false);

            // 폼 데이터 업데이트
            setForm(reception);

            // 모달 닫기
            if (close) setShowDlvModal(false);

            // alert(`${dlvModalType === 'inbound' ? '입고' : '출고'} 처리가 완료되었습니다.`);
        } catch (error) {
            console.error('DlvModal 저장 실패:', error);
            alert('처리에 실패했습니다.');
        }
    }, [onSave, dlvModalType]);

    // 4. DlvModal 닫기 핸들러
    const closeDlvModal = useCallback(() => {
        setShowDlvModal(false);
    }, []);

    const checkFirstLine = (item: ReceptionItemDTO, idx: number) => {
        const slice = form?.items?.slice(0, idx);
        const cnt = slice.filter(p => p.kind === item.kind).length;

        return cnt === 0;
    }

    if (!show) return null;

    return (
        <>
            {/* 모달 백드롭 */}
            <div
                className={`modal fade ${show ? 'show' : ''}`}
                style={{
                    display: show ? 'block' : 'none',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                }}
                tabIndex={-1}
                role="dialog"
            >
                <div className="modal-dialog modal-xl" role="document" style={{
                    maxWidth: window.innerWidth < 768 ? '95%' : '65%',
                    margin: window.innerWidth < 768 ? '0.5rem' : '1.75rem auto'
                }}>
                    <div className="modal-content">
                        {/* 헤더 */}
                        <div className="modal-header bg-primary">
                            <h4 className="modal-title text-white">
                                <i className="fas fa-file-alt mr-2"></i>
                                {title}
                            </h4>
                            <div className="d-flex align-items-center">
                                {form.updateAdmin && (
                                    <div className="text-white mr-3" style={{ fontSize: '0.875rem' }}>
                                        <i className="fas fa-user-edit mr-1"></i>
                                        <span>마지막 수정: {form.updateAdmin}</span>
                                        {form.updateAdmin && (
                                            <span className="ml-2" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                                                ({moment(form.updateAdmin).format('YYYY-MM-DD HH:mm')})
                                            </span>
                                        )}
                                    </div>
                                )}
                                <button type="button" className="close text-white" onClick={handleClose}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            {/* <button type="button" className="close text-white" onClick={handleClose}>
                                <span>&times;</span>
                            </button> */}
                        </div>

                        {/* 바디 */}
                        <div className="modal-body p-3" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                            {(loading || loadingData) && <Loading isWhole={false} />}

                            {/* 기본 정보 섹션 */}
                            <div className="card mb-3">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0">기본 정보</h5>
                                </div>
                                <div className="card-body">
                                    {/* 첫 번째 행 */}
                                    <div className="row mb-3">
                                        <div className="col-md-2">
                                            <label className="form-label">접수일자</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={form.receivedDate || ''}
                                                onChange={(e) => onChange('receivedDate', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">처리상황</label>
                                            <div className="form-control form-control-sm">
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={form.status}
                                                    onChange={(e) => {
                                                        onChange('status', e.target.value as ReceptionStatus)
                                                    }}
                                                    style={{ width: '100%', color: 'red', fontWeight: 'bold' }}
                                                >
                                                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label">처리유형</label>
                                            <div className="form-control form-control-sm">
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={form.type}
                                                    onChange={(e) => {
                                                        onChange('type', e.target.value as ReceptionType)
                                                        if (e.target.value as ReceptionType === ReceptionType.EXCHANGE || e.target.value as ReceptionType === ReceptionType.NEW_EXCHANGE) {
                                                            onChange('status', ReceptionStatus.OUTBOUND_READY);
                                                        }
                                                        // if (e.target.value as ReceptionType === ReceptionType.TRIP || e.target.value as ReceptionType === ReceptionType.COMP_SALE_TRIP) {
                                                        //     onChange('status', ReceptionStatus.TRIP_READY);
                                                        // }
                                                        if (e.target.value as ReceptionType === ReceptionType.SALE || e.target.value as ReceptionType === ReceptionType.COMP_SALE) {
                                                            onChange('status', ReceptionStatus.OUTBOUND_READY);
                                                        }
                                                    }}
                                                    style={{ width: '100%', color: 'red', fontWeight: 'bold' }}
                                                >
                                                    {Object.entries(TYPE_LABELS).map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <label className="form-label">결제내역</label>
                                            <div className="form-control form-control-sm">
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={form.paymentMethod || ''}
                                                    onChange={(e) => onChange('paymentMethod', e.target.value as PaymentMethod || null)}
                                                    style={{ width: '100%' }}
                                                >
                                                    <option value="">선택</option>
                                                    {Object.entries(PAYMENT_LABELS).map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <label className="form-label">증빙유형</label>
                                            <div className="form-control form-control-sm">
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={form.proofType || ''}
                                                    onChange={(e) => onChange('proofType', e.target.value as ProofType || null)}
                                                    style={{ width: '100%' }}
                                                >
                                                    <option value="">선택</option>
                                                    {Object.entries(PROOF_LABELS).map(([value, label]) => (
                                                        <option key={value} value={value}>{label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-md-1">
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-warning mr-2"
                                                    onClick={openTripCardModal}
                                                >
                                                    <i className="fas fa-car mr-1"></i>출장카드
                                                    {form?.tripCard?.content && <span className="badge badge-success ml-1">작성됨</span>}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="col-md-1">
                                            <label className="form-label">블랙리스트</label>
                                            <div className="form-check mt-2">
                                                <input
                                                    id="blacklist"
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!form.blacklist}
                                                    onChange={(e) => onChange('blacklist', e.target.checked)}
                                                />
                                                <label className="form-check-label" htmlFor="blacklist">블랙리스트 고객</label>
                                            </div>
                                        </div>

                                    </div>

                                    {/* 고객 정보 */}
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <label className="form-label">고객명</label>
                                            <input
                                                style={{ border: '3px solid #ccc', fontWeight: 'bold' }}
                                                className="form-control form-control-sm"
                                                value={form.customerName || ''}
                                                onChange={(e) => onChange('customerName', e.target.value)}
                                                placeholder="고객명을 입력하세요"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">연락처1 <span className="text-danger">*</span></label>
                                            <input
                                                style={{ border: '3px solid #ccc', fontWeight: 'bold' }}
                                                className="form-control form-control-sm"
                                                value={form.phone1 || ''}
                                                onChange={(e) => onChange('phone1', e.target.value)}
                                                placeholder="010-0000-0000"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">연락처2</label>
                                            <input
                                                style={{ border: '3px solid #ccc' }}
                                                className="form-control form-control-sm"
                                                value={form.phone2 || ''}
                                                onChange={(e) => onChange('phone2', e.target.value)}
                                                placeholder="보조 연락처"
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">연락처3</label>
                                            <input
                                                style={{ border: '3px solid #ccc' }}
                                                className="form-control form-control-sm"
                                                value={form.phone3 || ''}
                                                onChange={(e) => onChange('phone3', e.target.value)}
                                                placeholder="추가 연락처"
                                            />
                                        </div>
                                    </div>

                                    {/* 주소 정보 - 출장비 자동 적용 제거 */}
                                    {/* 주소 정보 섹션 */}
                                    <div className="row mb-3">
                                        {/* 도 입력 필드 */}
                                        <div className="col-md-2">
                                            <label className="form-label">도 (광역)</label>
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={form.province || ''}
                                                    onChange={(e) => handleProvinceChange(e.target.value)}
                                                    onFocus={handleProvinceFocus}
                                                    onBlur={handleProvinceBlur}
                                                    placeholder="도 입력 (예: 경기, 서울)"
                                                    autoComplete="off"
                                                    style={{ fontWeight: 'bold' }}
                                                />
                                                {/* {showProvinceSuggestions && provinceSuggestions.length > 0 && (
                                                    <div
                                                        className="position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
                                                        style={{
                                                            zIndex: 1000,
                                                            top: '100%',
                                                            maxHeight: '200px',
                                                            overflowY: 'auto'
                                                        }}
                                                    >
                                                        {provinceSuggestions.map((fee, idx) => (
                                                            <div
                                                                key={idx}
                                                                className="px-2 py-1 cursor-pointer hover:bg-light"
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.85rem',
                                                                    borderBottom: idx < provinceSuggestions.length - 1 ? '1px solid #dee2e6' : 'none'
                                                                }}
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => selectProvince(fee)}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                                }}
                                                            >
                                                                <div className="font-weight-bold">{fee.name}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )} */}
                                            </div>

                                        </div>

                                        {/* 지역 입력 필드 */}
                                        <div className="col-md-3">
                                            <label className="form-label">지역 (시/군/구)</label>
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={form.city || ''}
                                                    onChange={(e) => handleCityChange(e.target.value)}
                                                    onFocus={handleCityFocus}
                                                    onBlur={handleCityBlur}
                                                    placeholder="지역을 입력하세요"
                                                    autoComplete="off"
                                                    style={{ fontWeight: 'bold' }}
                                                />
                                                {/* {showCitySuggestions && citySuggestions.length > 0 && (
                                                    <div
                                                        className="position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
                                                        style={{
                                                            zIndex: 1000,
                                                            top: '100%',
                                                            maxHeight: '200px',
                                                            overflowY: 'auto'
                                                        }}
                                                    >
                                                        {citySuggestions.map((fee, idx) => (
                                                            <div
                                                                key={idx}
                                                                className="px-2 py-1 cursor-pointer hover:bg-light"
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.85rem',
                                                                    borderBottom: idx < citySuggestions.length - 1 ? '1px solid #dee2e6' : 'none'
                                                                }}
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => selectCity(fee)}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                                }}
                                                            >
                                                                <div className="font-weight-bold">{fee.name}</div>
                                                                {fee.tripCost && (
                                                                    <small className="text-info">
                                                                        출장 비용: {fee.tripCost.toLocaleString()}원
                                                                    </small>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )} */}
                                            </div>
                                            {/* {form.city && form.tripFeeAmount > 0 && (
                                                <small className="text-info">
                                                    출장 비용: {form.tripFeeAmount.toLocaleString()}원
                                                </small>
                                            )} */}
                                        </div>

                                        <div className="col-md-7">
                                            <label className="form-label">상세주소</label>
                                            <input
                                                className="form-control form-control-sm"
                                                value={form.address || ''}
                                                onChange={(e) => onChange('address', e.target.value)}
                                                placeholder="상세주소를 입력하세요"
                                            />
                                        </div>
                                    </div>

                                    {/* 접수/처리 내용 */}
                                    <div className="row">
                                        <div className="col-md-6">
                                            <label className="form-label">접수내용</label>
                                            <textarea
                                                style={{ border: '3px solid #00BA57', fontWeight: 'bold' }}
                                                className="form-control"
                                                rows={3}
                                                value={form.requestNote || ''}
                                                onChange={(e) => onChange('requestNote', e.target.value)}
                                                placeholder="고객 요청사항이나 증상을 입력하세요"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">처리내용</label>
                                            <textarea
                                                style={{ border: '3px solid #00BA57', fontWeight: 'bold' }}
                                                className="form-control"
                                                rows={3}
                                                value={form.handleNote || ''}
                                                onChange={(e) => onChange('handleNote', e.target.value)}
                                                placeholder="처리 결과나 진행사항을 입력하세요"
                                            />
                                        </div>
                                    </div>

                                    {/* 비고 내용 */}
                                    <div className="row">
                                        <div className="col-md-12">
                                            <label className="form-label">비고</label>
                                            <textarea
                                                style={{ border: '3px solid #ccc' }}
                                                className="form-control"
                                                rows={3}
                                                value={form.note || ''}
                                                onChange={(e) => onChange('note', e.target.value)}
                                                placeholder="비고 내용을 입력하세요"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card mb-3">
                                <div className="card-header text-white">
                                    <div className="float-left" style={{ width: '60%' }}>

                                        {/* 입고내역 */}
                                        <div className="input-group input-group-sm mb-1">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">입고내역</span>
                                            </div>
                                            <input
                                                type="text"
                                                className="form-control"
                                                disabled
                                                value={form.inboundNote || ''}
                                            />
                                        </div>

                                        {/* 출고내역 */}
                                        <div className="input-group input-group-sm">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">출고내역</span>
                                            </div>
                                            <input
                                                type="text"
                                                className="form-control"
                                                disabled
                                                value={form.outboundNote || ''}
                                            />
                                        </div>

                                    </div>

                                </div>
                            </div>


                            {/* 보일러 섹션 */}
                            <div className="card mb-3">
                                <div className="card-header bg-primary text-white d-flex justify-content-between">
                                    <h5 className="mb-0">보일러 항목</h5>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light mr-2"
                                            onClick={() => addItem(ReceptionItemKind.BOILER)}
                                        >
                                            <i className="fas fa-plus mr-1"></i>보일러 추가
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body p-0">
                                    {isMobile ? (
                                        // 모바일용 카드 레이아웃
                                        <div className="p-2">
                                            {(form.items || []).filter(item => item.kind === ReceptionItemKind.BOILER).length === 0 && (
                                                <div className="text-center text-muted py-4">
                                                    항목을 추가해주세요
                                                </div>
                                            )}
                                            {(form.items || []).filter(item => item.kind === ReceptionItemKind.BOILER).map((item, idx) => (
                                                <div key={idx} className="card mb-3" style={{
                                                    border: '3px solid #28a745',
                                                    borderRadius: '8px'
                                                }}>
                                                    <div className="card-header bg-success text-white p-2 d-flex justify-content-between align-items-center">
                                                        <span className="font-weight-bold">
                                                            <i className="fas fa-fire mr-1"></i>
                                                            보일러
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => removeItem(idx)}
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>

                                                    <div className="card-body p-2">
                                                        {/* 유형 */}
                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                유형
                                                            </label>
                                                            <select
                                                                disabled={true}
                                                                name="kind"
                                                                value={item.kind}
                                                                onChange={(e) => changeItem(idx, { kind: ReceptionItemKind[e.target.value] })}
                                                                className="form-control form-control-sm"
                                                            >
                                                                <option value="BOILER">보일러</option>
                                                                <option value="SALE">판매</option>
                                                                <option value="DISCOUNT">할인</option>
                                                            </select>
                                                        </div>

                                                        {/* 날짜 정보 */}
                                                        <div className="row mb-2">
                                                            <div className="col-6">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    택배출고일
                                                                </label>
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={item.courierOutboundDate || ''}
                                                                    onChange={(e) => changeItem(idx, { courierOutboundDate: e.target.value })}
                                                                />
                                                            </div>
                                                            <div className="col-6">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    택배입고일
                                                                </label>
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={item.courierInboundDate || ''}
                                                                    onChange={(e) => changeItem(idx, { courierInboundDate: e.target.value })}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                수리일자
                                                            </label>
                                                            <input
                                                                type="date"
                                                                className="form-control form-control-sm"
                                                                value={item.repairDate || ''}
                                                                onChange={(e) => changeItem(idx, { repairDate: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* 보일러 정보 */}
                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'red' }}>
                                                                보일러명 *
                                                            </label>
                                                            <div className="position-relative">
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={item.modelName || ''}
                                                                    onChange={(e) => handleBoilerNameChange(idx, e.target.value)}
                                                                    onKeyDown={(e) => handleBoilerNameKeyDown(idx, e)}
                                                                    onFocus={() => handleBoilerNameFocus(idx)}
                                                                    onBlur={() => handleBoilerNameBlur(idx)}
                                                                    placeholder="보일러명을 입력하세요"
                                                                    autoComplete="off"
                                                                    style={{ color: 'red', fontWeight: 'bold' }}
                                                                />
                                                                {showBoilerSuggestions[idx] && boilerNameSuggestions[idx]?.length > 0 && (
                                                                    <div
                                                                        className="position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
                                                                        style={{
                                                                            zIndex: 1000,
                                                                            top: '100%',
                                                                            marginTop: '2px',
                                                                            maxHeight: '150px',
                                                                            overflowY: 'auto'
                                                                        }}
                                                                    >
                                                                        {boilerNameSuggestions[idx].map((product, suggestionIdx) => {
                                                                            const isSelected = selectedSuggestionIndex[idx] === suggestionIdx;
                                                                            return (
                                                                                <div
                                                                                    key={product.id}
                                                                                    data-suggestion-index={`${idx}-${suggestionIdx}`}
                                                                                    className="px-2 py-1"
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                        fontSize: '0.85rem',
                                                                                        borderBottom: suggestionIdx < boilerNameSuggestions[idx].length - 1 ? '1px solid #dee2e6' : 'none',
                                                                                        backgroundColor: isSelected ? '#e7f3ff' : 'transparent',
                                                                                        fontWeight: isSelected ? 'bold' : 'normal'
                                                                                    }}
                                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                                    onClick={() => selectBoilerName(idx, product)}
                                                                                    onMouseEnter={() => {
                                                                                        setSelectedSuggestionIndex(prev => ({ ...prev, [idx]: suggestionIdx }));
                                                                                    }}
                                                                                >
                                                                                    <div className="font-weight-bold">{product.productName}</div>
                                                                                    <small className="text-muted">{product.productCode}</small>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'red' }}>
                                                                제조년월
                                                            </label>
                                                            <input
                                                                type="month"
                                                                className="form-control form-control-sm"
                                                                value={item.manufactureMonth || ''}
                                                                onChange={(e) => changeItem(idx, { manufactureMonth: e.target.value })}
                                                                style={{ color: 'red', fontWeight: 'bold' }}
                                                            />
                                                        </div>

                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'red' }}>
                                                                특이사항
                                                            </label>
                                                            <input
                                                                className="form-control form-control-sm"
                                                                value={item.note || ''}
                                                                onChange={(e) => changeItem(idx, { note: e.target.value })}
                                                                placeholder="특이사항"
                                                                style={{ color: 'red', fontWeight: 'bold' }}
                                                            />
                                                        </div>

                                                        {/* 구분선 */}
                                                        <hr className="my-2" style={{ borderTop: '2px solid #dee2e6' }} />

                                                        {/* 물류 정보 */}
                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                입고경로
                                                            </label>
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={item.inboundMethod || ''}
                                                                onChange={(e) => changeItem(idx, { inboundMethod: e.target.value as InboundMethod })}
                                                            >
                                                                <option value="">선택</option>
                                                                <option value="COURIER">택배</option>
                                                                <option value="TRIP">출장</option>
                                                                <option value="VISIT">방문</option>
                                                            </select>
                                                        </div>

                                                        <div className="row mb-2">
                                                            <div className="col-6">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'red' }}>
                                                                    입고/택발
                                                                </label>
                                                                <select
                                                                    className="form-control form-control-sm"
                                                                    value={item.flow || ''}
                                                                    onChange={(e) => {
                                                                        changeItem(idx, { flow: e.target.value as FlowType })
                                                                        if (e.target.value === 'OUTBOUND_COURIER') {
                                                                            changeItem(idx, { dlvFee: 5000 });
                                                                        }
                                                                    }}
                                                                    style={{ color: 'red', fontWeight: 'bold' }}
                                                                >
                                                                    <option value="">선택</option>
                                                                    <option value="INBOUND">입고</option>
                                                                    <option value="OUTBOUND_COURIER">택발</option>
                                                                </select>
                                                            </div>
                                                            <div className="col-6">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    선불/착불
                                                                </label>
                                                                <select
                                                                    className="form-control form-control-sm"
                                                                    value={item.dlvFee != 0 && !item.dlvFee ? "" : item.dlvFee}
                                                                    onChange={(e) => {
                                                                        changeItem(idx, { dlvFee: e.target.value === "" ? null : Number(e.target.value) });
                                                                    }}
                                                                >
                                                                    <option value="">선택</option>
                                                                    <option value="5000">선불</option>
                                                                    <option value="0">착불</option>
                                                                </select>
                                                            </div>
                                                        </div>

                                                        {/* <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                운송사
                                                            </label>
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={useCustomDlvCompany[idx] ? '직접입력' : (item.dlvCompany || '')}
                                                                onChange={(e) => onDlvCompanyChange(idx, e.target.value)}
                                                            >
                                                                <option value="">선택</option>
                                                                {deliveryCompanyOptions.map(option => (
                                                                    <option key={option.name} value={option.name}>
                                                                        {option.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {useCustomDlvCompany[idx] && (
                                                            <div className="mb-2">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    운송사 직접입력
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={customDlvCompany[idx] || ''}
                                                                    onChange={(e) => onCustomDlvCompanyChange(idx, e.target.value)}
                                                                    placeholder="운송사명 입력"
                                                                />
                                                            </div>
                                                        )} */}

                                                        {/* 수리/판매 내역 */}
                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                수리내역
                                                            </label>
                                                            <input
                                                                className="form-control form-control-sm"
                                                                value={getItemSummary(item)}
                                                                placeholder="수리내역"
                                                                readOnly={true}
                                                                onClick={() => openRepairModal(idx)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    backgroundColor: '#f8f9fa',
                                                                    minHeight: '38px'
                                                                }}
                                                            />
                                                        </div>

                                                        {/* 금액 정보 */}
                                                        <div className="row mb-2">
                                                            <div className="col-6">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    비용
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={item.totalAmount || 0}
                                                                    onChange={(e) => changeItem(idx, { totalAmount: Number(e.target.value) || 0 })}
                                                                />
                                                            </div>
                                                            <div className="col-6">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    원가
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={item.totalCost || 0}
                                                                    onChange={(e) => changeItem(idx, { totalCost: Number(e.target.value) || 0 })}
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* 작업 버튼 */}
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary flex-fill"
                                                                onClick={() => openRepairModal(idx)}
                                                            >
                                                                <i className="fas fa-list mr-1"></i>수리목록
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="table-responsive" style={{ overflow: 'visible' }}>
                                            <table className="table table-sm mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                <tbody>
                                                    {(form.items || []).filter(item => item.kind === ReceptionItemKind.BOILER).length === 0 && (
                                                        <tr>
                                                            <td colSpan={20} className="text-center text-muted py-4">
                                                                항목을 추가해주세요
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {(form.items || []).map((item, idx) => (
                                                        item.kind === ReceptionItemKind.BOILER && (
                                                            <React.Fragment key={idx}>
                                                                {/* 항목 구분선 (첫 번째 항목 제외) */}
                                                                {!checkFirstLine(item, idx) && (
                                                                    <tr>
                                                                        <td colSpan={20} className="p-0">
                                                                            <div style={{
                                                                                borderTop: '3px solid #28a745',  // 녹색 테두리로 변경
                                                                                margin: '8px 0'  // 상하 여백 추가
                                                                            }}></div>
                                                                        </td>
                                                                    </tr>
                                                                )}

                                                                {/* 첫 번째 행 - 헤더 */}
                                                                <tr className="bg-light" style={{
                                                                    borderTop: '3px solid #28a745',  // 상단 테두리 강화
                                                                    borderLeft: '3px solid #28a745',  // 좌측 테두리
                                                                    borderRight: '3px solid #28a745'  // 우측 테두리
                                                                }}>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '8%' }}>유형</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '12%' }}>택배출고일</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '12%' }}>택배입고일</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '12%' }}>수리일자</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '13%' }}>보일러명</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '15%' }}>제조년월</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '18%' }}>특이사항</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '10%' }}></th>
                                                                </tr>

                                                                {/* 첫 번째 행 - 데이터 */}
                                                                <tr className="hover-bg-light" style={{
                                                                    borderLeft: '3px solid #28a745',  // 좌측 테두리
                                                                    borderRight: '3px solid #28a745'  // 우측 테두리
                                                                }}>
                                                                    <td className="px-2 py-2">
                                                                        <select
                                                                            disabled={true}
                                                                            name="kind"
                                                                            value={item.kind}
                                                                            onChange={(e) => changeItem(idx, { kind: ReceptionItemKind[e.target.value] })}
                                                                            className="form-control form-control-sm"
                                                                        >
                                                                            <option value="BOILER">보일러</option>
                                                                            <option value="SALE">판매</option>
                                                                            <option value="DISCOUNT">할인</option>
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <input
                                                                            type="date"
                                                                            className="form-control form-control-sm"
                                                                            value={item.courierOutboundDate || ''}
                                                                            onChange={(e) => changeItem(idx, { courierOutboundDate: e.target.value })}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <input
                                                                            type="date"
                                                                            className="form-control form-control-sm"
                                                                            value={item.courierInboundDate || ''}
                                                                            onChange={(e) => changeItem(idx, { courierInboundDate: e.target.value })}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <input
                                                                            type="date"
                                                                            className="form-control form-control-sm"
                                                                            value={item.repairDate || ''}
                                                                            onChange={(e) => changeItem(idx, { repairDate: e.target.value })}
                                                                            placeholder="수리일자"
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <div className="position-relative">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={item.modelName || ''}
                                                                                onChange={(e) => handleBoilerNameChange(idx, e.target.value)}
                                                                                onKeyDown={(e) => handleBoilerNameKeyDown(idx, e)}
                                                                                onFocus={() => handleBoilerNameFocus(idx)}
                                                                                onBlur={() => handleBoilerNameBlur(idx)}
                                                                                placeholder="보일러명을 입력하세요"
                                                                                autoComplete="off"
                                                                                style={{ color: 'red', fontWeight: 'bold' }}
                                                                            />
                                                                            {showBoilerSuggestions[idx] && boilerNameSuggestions[idx]?.length > 0 && (
                                                                                <div
                                                                                    className="position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
                                                                                    style={{
                                                                                        zIndex: 1000,
                                                                                        bottom: '100%',  // 상단으로 표시되도록 변경
                                                                                        marginBottom: '2px',  // 입력 필드와의 간격
                                                                                        maxHeight: '200px',
                                                                                        overflowY: 'auto'
                                                                                    }}
                                                                                >
                                                                                    {boilerNameSuggestions[idx].map((product, suggestionIdx) => {
                                                                                        const isSelected = selectedSuggestionIndex[idx] === suggestionIdx;
                                                                                        return (
                                                                                            <div
                                                                                                key={product.id}
                                                                                                data-suggestion-index={`${idx}-${suggestionIdx}`}
                                                                                                className="px-2 py-1 cursor-pointer hover:bg-light"
                                                                                                style={{
                                                                                                    cursor: 'pointer',
                                                                                                    fontSize: '0.85rem',
                                                                                                    borderBottom: suggestionIdx < boilerNameSuggestions[idx].length - 1 ? '1px solid #dee2e6' : 'none',
                                                                                                    backgroundColor: isSelected ? '#e7f3ff' : 'transparent',  // 선택된 항목 하이라이트
                                                                                                    fontWeight: isSelected ? 'bold' : 'normal'
                                                                                                }}
                                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                                                onClick={() => selectBoilerName(idx, product)}
                                                                                                onMouseEnter={() => {
                                                                                                    setSelectedSuggestionIndex(prev => ({ ...prev, [idx]: suggestionIdx }));
                                                                                                }}
                                                                                            >
                                                                                                <div className="font-weight-bold">{product.productName}</div>
                                                                                                <small className="text-muted">{product.productCode}</small>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <input
                                                                            type="month"
                                                                            className="form-control form-control-sm"
                                                                            value={item.manufactureMonth || ''}
                                                                            onChange={(e) => changeItem(idx, { manufactureMonth: e.target.value })}
                                                                            style={{ color: 'red', fontWeight: 'bold' }}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <input
                                                                            className="form-control form-control-sm"
                                                                            value={item.note || ''}
                                                                            onChange={(e) => changeItem(idx, { note: e.target.value })}
                                                                            placeholder="특이사항"
                                                                            style={{ color: 'red', fontWeight: 'bold' }}
                                                                        />
                                                                    </td>
                                                                </tr>

                                                                {/* 두 번째 행 - 헤더 */}
                                                                <tr className="bg-light" style={{
                                                                    backgroundColor: '#e7f3ff',
                                                                    borderLeft: '3px solid #28a745',
                                                                    borderRight: '3px solid #28a745'
                                                                }}>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '100px' }}>입고경로</th>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '120px' }}>입고/택발</th>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '120px' }}>선불/착불</th>
                                                                    {/* <th className="px-3 py-2 text-nowrap" style={{ minWidth: '140px' }}>운송사</th> */}
                                                                    {/* <th className="px-3 py-2 text-nowrap" style={{ minWidth: '120px' }}>운송사 직접입력</th> */}
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '250px' }}>수리내역</th>
                                                                    {/* <th className="px-3 py-2 text-nowrap" style={{ minWidth: '140px' }}>비용/원가</th> */}
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '100px' }}>작업</th>
                                                                </tr>

                                                                {/* 두 번째 행 - 데이터 */}
                                                                <tr style={{
                                                                    borderBottom: '3px solid #28a745',  // 하단 테두리 강화
                                                                    borderLeft: '3px solid #28a745',
                                                                    borderRight: '3px solid #28a745'
                                                                }}>
                                                                    <td className="px-2 py-2">
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            value={item.inboundMethod || ''}
                                                                            onChange={(e) => changeItem(idx, { inboundMethod: e.target.value as InboundMethod })}
                                                                        >
                                                                            <option value="">선택</option>
                                                                            <option value="COURIER">택배</option>
                                                                            <option value="TRIP">출장</option>
                                                                            <option value="VISIT">방문</option>
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            value={item.flow || ''}
                                                                            onChange={
                                                                                (e) => {
                                                                                    changeItem(idx, { flow: e.target.value as FlowType })
                                                                                    if (e.target.value === 'OUTBOUND_COURIER') {
                                                                                        changeItem(idx, { dlvFee: 5000 });
                                                                                    }
                                                                                }
                                                                            }
                                                                            style={{ color: 'red', fontWeight: 'bold' }}
                                                                        >
                                                                            <option value="">선택</option>
                                                                            <option value="INBOUND">입고</option>
                                                                            <option value="OUTBOUND_COURIER">택발</option>
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            value={item.dlvFee != 0 && !item.dlvFee ? "" : item.dlvFee}
                                                                            onChange={(e) => {
                                                                                changeItem(idx, { dlvFee: e.target.value === "" ? null : Number(e.target.value) });
                                                                            }}
                                                                        >
                                                                            <option value="">선택</option>
                                                                            <option value="5000">선불</option>
                                                                            <option value="0">착불</option>
                                                                        </select>
                                                                    </td>
                                                                    {/* <td className="px-2 py-2">
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            value={useCustomDlvCompany[idx] ? '직접입력' : (item.dlvCompany || '')}
                                                                            onChange={(e) => onDlvCompanyChange(idx, e.target.value)}
                                                                        >
                                                                            <option value="">선택</option>
                                                                            {deliveryCompanyOptions.map(option => (
                                                                                <option key={option.name} value={option.name}>
                                                                                    {option.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {useCustomDlvCompany[idx] && (
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={customDlvCompany[idx] || ''}
                                                                                onChange={(e) => onCustomDlvCompanyChange(idx, e.target.value)}
                                                                                placeholder="운송사명 입력"
                                                                            />
                                                                        )}
                                                                    </td> */}
                                                                    <td className="px-2 py-2">
                                                                        <input
                                                                            className="form-control form-control-sm"
                                                                            value={getItemSummary(item)}
                                                                            placeholder="수리내역"
                                                                            readOnly={true}
                                                                            onClick={() => openRepairModal(idx)}
                                                                            style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                                                        />
                                                                    </td>
                                                                    {/* <td className="px-2 py-2">
                                                                        <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                                                            <div className="input-group input-group-sm">
                                                                                <div className="input-group-prepend">
                                                                                    <span className="input-group-text" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>비용</span>
                                                                                </div>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    value={item.totalAmount || 0}
                                                                                    onChange={(e) => changeItem(idx, { totalAmount: Number(e.target.value) || 0 })}
                                                                                    style={{ fontSize: '0.875rem' }}
                                                                                />
                                                                            </div>
                                                                            <div className="input-group input-group-sm">
                                                                                <div className="input-group-prepend">
                                                                                    <span className="input-group-text" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>원가</span>
                                                                                </div>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    value={item.totalCost || 0}
                                                                                    onChange={(e) => changeItem(idx, { totalCost: Number(e.target.value) || 0 })}
                                                                                    style={{ fontSize: '0.875rem' }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </td> */}
                                                                    <td className="px-2 py-2">
                                                                        <div className="d-flex" style={{ gap: '4px' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-primary"
                                                                                onClick={() => openRepairModal(idx)}
                                                                                title="수리/판매목록"
                                                                            >
                                                                                <i className="fas fa-list"></i>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-danger"
                                                                                onClick={() => removeItem(idx)}
                                                                                title="삭제"
                                                                            >
                                                                                <i className="fas fa-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        )))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                </div>

                                {/* 판매 섹션 */}
                                <div className="card-header bg-primary text-white d-flex justify-content-between">
                                    <h5 className="mb-0">판매 항목</h5>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light"
                                            onClick={() => addItem(ReceptionItemKind.SALE)}
                                        >
                                            <i className="fas fa-plus mr-1"></i>판매 추가
                                        </button>
                                    </div>
                                </div>

                                <div className="card-body p-0">

                                    {/* 판매 섹션 - 모바일 카드 */}
                                    {isMobile ? (
                                        <div className="p-2">
                                            {(form.items || []).filter(item => item.kind !== ReceptionItemKind.BOILER).length === 0 && (
                                                <div className="text-center text-muted py-4">
                                                    항목을 추가해주세요
                                                </div>
                                            )}
                                            {(form.items || []).map((item, idx) => (
                                                item.kind !== ReceptionItemKind.BOILER && (
                                                    <div key={idx} className="card mb-3" style={{
                                                        border: '3px solid #28a745',
                                                        borderRadius: '8px'
                                                    }}>
                                                        <div className="card-header bg-info text-white p-2 d-flex justify-content-between align-items-center">
                                                            <span className="font-weight-bold">
                                                                <i className={`fas ${item.kind === ReceptionItemKind.DISCOUNT ? 'fa-percentage' : 'fa-shopping-cart'} mr-1`}></i>
                                                                {item.kind === ReceptionItemKind.DISCOUNT ? '할인' : '판매'}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => removeItem(idx)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>

                                                        <div className="card-body p-2">
                                                            {/* 유형 */}
                                                            <div className="mb-2">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    유형
                                                                </label>
                                                                <select
                                                                    disabled={true}
                                                                    name="kind"
                                                                    value={item.kind}
                                                                    onChange={(e) => changeItem(idx, { kind: ReceptionItemKind[e.target.value] })}
                                                                    className="form-control form-control-sm"
                                                                >
                                                                    <option value="BOILER">보일러</option>
                                                                    <option value="SALE">판매</option>
                                                                    <option value="DISCOUNT">할인</option>
                                                                </select>
                                                            </div>

                                                            {item.kind !== ReceptionItemKind.DISCOUNT && (
                                                                <>
                                                                    {/* 택배출고일 */}
                                                                    <div className="mb-2">
                                                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                            택배출고일
                                                                        </label>
                                                                        <input
                                                                            type="date"
                                                                            className="form-control form-control-sm"
                                                                            value={item.courierOutboundDate || ''}
                                                                            onChange={(e) => changeItem(idx, { courierOutboundDate: e.target.value })}
                                                                        />
                                                                    </div>

                                                                    {/* 보일러명 */}
                                                                    <div className="mb-2">
                                                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'red' }}>
                                                                            보일러명/상품명 *
                                                                        </label>
                                                                        <div className="position-relative">
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={item.modelName || ''}
                                                                                onChange={(e) => handleBoilerNameChange(idx, e.target.value)}
                                                                                onKeyDown={(e) => handleBoilerNameKeyDown(idx, e)}
                                                                                onFocus={() => handleBoilerNameFocus(idx)}
                                                                                onBlur={() => handleBoilerNameBlur(idx)}
                                                                                placeholder="보일러명을 입력하세요"
                                                                                autoComplete="off"
                                                                                style={{ color: 'red', fontWeight: 'bold' }}
                                                                            />
                                                                            {showBoilerSuggestions[idx] && boilerNameSuggestions[idx]?.length > 0 && (
                                                                                <div
                                                                                    className="position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
                                                                                    style={{
                                                                                        zIndex: 1000,
                                                                                        top: '100%',
                                                                                        marginTop: '2px',
                                                                                        maxHeight: '150px',
                                                                                        overflowY: 'auto'
                                                                                    }}
                                                                                >
                                                                                    {boilerNameSuggestions[idx].map((product, suggestionIdx) => {
                                                                                        const isSelected = selectedSuggestionIndex[idx] === suggestionIdx;
                                                                                        return (
                                                                                            <div
                                                                                                key={product.id}
                                                                                                data-suggestion-index={`${idx}-${suggestionIdx}`}
                                                                                                className="px-2 py-1"
                                                                                                style={{
                                                                                                    cursor: 'pointer',
                                                                                                    fontSize: '0.85rem',
                                                                                                    borderBottom: suggestionIdx < boilerNameSuggestions[idx].length - 1 ? '1px solid #dee2e6' : 'none',
                                                                                                    backgroundColor: isSelected ? '#e7f3ff' : 'transparent',
                                                                                                    fontWeight: isSelected ? 'bold' : 'normal'
                                                                                                }}
                                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                                                onClick={() => selectBoilerName(idx, product)}
                                                                                                onMouseEnter={() => {
                                                                                                    setSelectedSuggestionIndex(prev => ({ ...prev, [idx]: suggestionIdx }));
                                                                                                }}
                                                                                            >
                                                                                                <div className="font-weight-bold">{product.productName}</div>
                                                                                                <small className="text-muted">{product.productCode}</small>
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* 제조년월 */}
                                                                    <div className="mb-2">
                                                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'red' }}>
                                                                            제조년월
                                                                        </label>
                                                                        <input
                                                                            type="month"
                                                                            className="form-control form-control-sm"
                                                                            value={item.manufactureMonth || ''}
                                                                            onChange={(e) => changeItem(idx, { manufactureMonth: e.target.value })}
                                                                            style={{ color: 'red', fontWeight: 'bold' }}
                                                                        />
                                                                    </div>

                                                                    {/* 특이사항 */}
                                                                    <div className="mb-2">
                                                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'red' }}>
                                                                            특이사항
                                                                        </label>
                                                                        <input
                                                                            className="form-control form-control-sm"
                                                                            value={item.note || ''}
                                                                            onChange={(e) => changeItem(idx, { note: e.target.value })}
                                                                            placeholder="특이사항"
                                                                            style={{ color: 'red', fontWeight: 'bold' }}
                                                                        />
                                                                    </div>

                                                                    {/* 구분선 */}
                                                                    <hr className="my-2" style={{ borderTop: '2px solid #dee2e6' }} />

                                                                    {/* 선불/착불 */}
                                                                    <div className="mb-2">
                                                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                            선불/착불
                                                                        </label>
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            value={item.dlvFee != 0 && !item.dlvFee ? "" : item.dlvFee}
                                                                            onChange={(e) => {
                                                                                changeItem(idx, { dlvFee: e.target.value === "" ? null : Number(e.target.value) });
                                                                            }}
                                                                        >
                                                                            <option value="">선택</option>
                                                                            <option value="5000">선불</option>
                                                                            <option value="0">착불</option>
                                                                        </select>
                                                                    </div>

                                                                    {/* 운송사 */}
                                                                    <div className="mb-2">
                                                                        <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                            운송사
                                                                        </label>
                                                                        <select
                                                                            className="form-control form-control-sm"
                                                                            value={useCustomDlvCompany[idx] ? '직접입력' : (item.dlvCompany || '')}
                                                                            onChange={(e) => onDlvCompanyChange(idx, e.target.value)}
                                                                        >
                                                                            <option value="">선택</option>
                                                                            {deliveryCompanyOptions.map(option => (
                                                                                <option key={option.name} value={option.name}>
                                                                                    {option.name}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    </div>

                                                                    {useCustomDlvCompany[idx] && (
                                                                        <div className="mb-2">
                                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                                운송사 직접입력
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={customDlvCompany[idx] || ''}
                                                                                onChange={(e) => onCustomDlvCompanyChange(idx, e.target.value)}
                                                                                placeholder="운송사명 입력"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}

                                                            {/* 수리/판매 내역 */}
                                                            <div className="mb-2">
                                                                <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                    {item.kind === ReceptionItemKind.DISCOUNT ? '할인내역' : '판매내역'}
                                                                </label>
                                                                <input
                                                                    className="form-control form-control-sm"
                                                                    value={getItemSummary(item)}
                                                                    placeholder={item.kind === ReceptionItemKind.DISCOUNT ? '할인내역' : '판매내역'}
                                                                    readOnly={true}
                                                                    onClick={() => openRepairModal(idx)}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        backgroundColor: '#f8f9fa',
                                                                        minHeight: '38px'
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* 금액 정보 */}
                                                            <div className="row mb-2">
                                                                <div className="col-6">
                                                                    <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                        비용
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.totalAmount || 0}
                                                                        onChange={(e) => changeItem(idx, { totalAmount: Number(e.target.value) || 0 })}
                                                                    />
                                                                </div>
                                                                <div className="col-6">
                                                                    <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                        원가
                                                                    </label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control form-control-sm"
                                                                        value={item.totalCost || 0}
                                                                        onChange={(e) => changeItem(idx, { totalCost: Number(e.target.value) || 0 })}
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* 작업 버튼 */}
                                                            <div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary w-100"
                                                                    onClick={() => openRepairModal(idx)}
                                                                >
                                                                    <i className="fas fa-list mr-1"></i>
                                                                    {item.kind === ReceptionItemKind.DISCOUNT ? '할인' : '판매'}목록
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="table-responsive" style={{ overflow: 'visible' }}>
                                            <table className="table table-sm mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                                <tbody>
                                                    {(form.items || []).filter(item => item.kind !== ReceptionItemKind.BOILER).length === 0 && (
                                                        <tr>
                                                            <td colSpan={20} className="text-center text-muted py-4">
                                                                항목을 추가해주세요
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {(form.items || []).map((item, idx) => (
                                                        item.kind !== ReceptionItemKind.BOILER && (
                                                            <React.Fragment key={idx}>
                                                                {/* 항목 구분선 (첫 번째 항목 제외) */}
                                                                {!checkFirstLine(item, idx) && (
                                                                    <tr>
                                                                        <td colSpan={20} className="p-0">
                                                                            <div style={{
                                                                                borderTop: '3px solid #28a745',  // 녹색 테두리로 변경
                                                                                margin: '8px 0'  // 상하 여백 추가
                                                                            }}></div>
                                                                        </td>
                                                                    </tr>
                                                                )}

                                                                {/* 첫 번째 행 - 헤더 */}
                                                                <tr className="bg-light" style={{
                                                                    borderTop: '3px solid #28a745',  // 상단 테두리 강화
                                                                    borderLeft: '3px solid #28a745',  // 좌측 테두리
                                                                    borderRight: '3px solid #28a745'  // 우측 테두리
                                                                }}>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '8%' }}>유형</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '12%' }}>택배출고일</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '12%' }}>택배입고일</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '12%' }}>수리일자</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '18%' }}>보일러명</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '10%' }}>제조년월</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '18%' }}>특이사항</th>
                                                                    <th className="px-2 py-2 text-nowrap" style={{ width: '10%' }}></th>
                                                                </tr>

                                                                {/* 첫 번째 행 - 데이터 */}
                                                                <tr className="hover-bg-light" style={{
                                                                    borderLeft: '3px solid #28a745',  // 좌측 테두리
                                                                    borderRight: '3px solid #28a745'  // 우측 테두리
                                                                }}>
                                                                    <td className="px-2 py-2">
                                                                        <select
                                                                            disabled={true}
                                                                            name="kind"
                                                                            value={item.kind}
                                                                            onChange={(e) => changeItem(idx, { kind: ReceptionItemKind[e.target.value] })}
                                                                            className="form-control form-control-sm"
                                                                        >
                                                                            <option value="BOILER">보일러</option>
                                                                            <option value="SALE">판매</option>
                                                                            <option value="DISCOUNT">할인</option>
                                                                        </select>
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {item.kind !== ReceptionItemKind.DISCOUNT && (
                                                                            <input
                                                                                type="date"
                                                                                className="form-control form-control-sm"
                                                                                value={item.courierOutboundDate || ''}
                                                                                onChange={(e) => changeItem(idx, { courierOutboundDate: e.target.value })}
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 py-2">

                                                                    </td>
                                                                    <td className="px-2 py-2">

                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {item.kind !== ReceptionItemKind.DISCOUNT && (
                                                                            <td className="px-2 py-2">
                                                                                <div className="position-relative">
                                                                                    <input
                                                                                        type="text"
                                                                                        className="form-control form-control-sm"
                                                                                        value={item.modelName || ''}
                                                                                        onChange={(e) => handleBoilerNameChange(idx, e.target.value)}
                                                                                        onKeyDown={(e) => handleBoilerNameKeyDown(idx, e)}
                                                                                        onFocus={() => handleBoilerNameFocus(idx)}
                                                                                        onBlur={() => handleBoilerNameBlur(idx)}
                                                                                        placeholder="보일러명을 입력하세요"
                                                                                        autoComplete="off"
                                                                                        style={{ color: 'red', fontWeight: 'bold' }}
                                                                                    />
                                                                                    {showBoilerSuggestions[idx] && boilerNameSuggestions[idx]?.length > 0 && (
                                                                                        <div
                                                                                            className="position-absolute w-100 bg-white border border-secondary rounded shadow-sm"
                                                                                            style={{
                                                                                                zIndex: 1000,
                                                                                                bottom: '100%',  // 상단으로 표시되도록 변경
                                                                                                marginBottom: '2px',  // 입력 필드와의 간격
                                                                                                maxHeight: '200px',
                                                                                                overflowY: 'auto'
                                                                                            }}
                                                                                        >
                                                                                            {boilerNameSuggestions[idx].map((product, suggestionIdx) => {
                                                                                                const isSelected = selectedSuggestionIndex[idx] === suggestionIdx;
                                                                                                return (
                                                                                                    <div
                                                                                                        key={product.id}
                                                                                                        data-suggestion-index={`${idx}-${suggestionIdx}`}
                                                                                                        className="px-2 py-1 cursor-pointer hover:bg-light"
                                                                                                        style={{
                                                                                                            cursor: 'pointer',
                                                                                                            fontSize: '0.85rem',
                                                                                                            borderBottom: suggestionIdx < boilerNameSuggestions[idx].length - 1 ? '1px solid #dee2e6' : 'none',
                                                                                                            backgroundColor: isSelected ? '#e7f3ff' : 'transparent',  // 선택된 항목 하이라이트
                                                                                                            fontWeight: isSelected ? 'bold' : 'normal'
                                                                                                        }}
                                                                                                        onMouseDown={(e) => e.preventDefault()}
                                                                                                        onClick={() => selectBoilerName(idx, product)}
                                                                                                        onMouseEnter={() => {
                                                                                                            setSelectedSuggestionIndex(prev => ({ ...prev, [idx]: suggestionIdx }));
                                                                                                        }}
                                                                                                    >
                                                                                                        <div className="font-weight-bold">{product.productName}</div>
                                                                                                        <small className="text-muted">{product.productCode}</small>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {item.kind !== ReceptionItemKind.DISCOUNT && (
                                                                            <input
                                                                                type="month"
                                                                                className="form-control form-control-sm"
                                                                                value={item.manufactureMonth || ''}
                                                                                onChange={(e) => changeItem(idx, { manufactureMonth: e.target.value })}
                                                                                style={{ color: 'red', fontWeight: 'bold' }}
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {item.kind !== ReceptionItemKind.DISCOUNT && (
                                                                            <input
                                                                                className="form-control form-control-sm"
                                                                                value={item.note || ''}
                                                                                onChange={(e) => changeItem(idx, { note: e.target.value })}
                                                                                placeholder="특이사항"
                                                                                style={{ color: 'red', fontWeight: 'bold' }}
                                                                            />
                                                                        )}
                                                                    </td>

                                                                </tr>

                                                                {/* 두 번째 행 - 헤더 */}
                                                                <tr className="bg-light" style={{
                                                                    backgroundColor: '#e7f3ff',
                                                                    borderLeft: '3px solid #28a745',
                                                                    borderRight: '3px solid #28a745'
                                                                }}>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '100px' }}>입고경로</th>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '120px' }}>입고/택발</th>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '120px' }}>선불/착불</th>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '140px' }}>운송사</th>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '120px' }}>운송사 직접입력</th>
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '250px' }}>판매내역</th>
                                                                    {/* <th className="px-3 py-2 text-nowrap" style={{ minWidth: '140px' }}>비용/원가</th> */}
                                                                    <th className="px-3 py-2 text-nowrap" style={{ minWidth: '100px' }}>작업</th>
                                                                </tr>

                                                                {/* 두 번째 행 - 데이터 */}
                                                                <tr style={{
                                                                    borderBottom: '3px solid #28a745',  // 하단 테두리 강화
                                                                    borderLeft: '3px solid #28a745',
                                                                    borderRight: '3px solid #28a745'
                                                                }}>
                                                                    <td className="px-2 py-2">

                                                                    </td>
                                                                    <td className="px-2 py-2">

                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {item.kind !== ReceptionItemKind.DISCOUNT && (
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                value={item.dlvFee != 0 && !item.dlvFee ? "" : item.dlvFee}
                                                                                onChange={(e) => {
                                                                                    changeItem(idx, { dlvFee: e.target.value === "" ? null : Number(e.target.value) });
                                                                                }}
                                                                            >
                                                                                <option value="">선택</option>
                                                                                <option value="5000">선불</option>
                                                                                <option value="0">착불</option>
                                                                            </select>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {item.kind !== ReceptionItemKind.DISCOUNT && (
                                                                            <select
                                                                                className="form-control form-control-sm"
                                                                                value={useCustomDlvCompany[idx] ? '직접입력' : (item.dlvCompany || '')}
                                                                                onChange={(e) => onDlvCompanyChange(idx, e.target.value)}
                                                                            >
                                                                                <option value="">선택</option>
                                                                                {deliveryCompanyOptions.map(option => (
                                                                                    <option key={option.name} value={option.name}>
                                                                                        {option.name}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        {useCustomDlvCompany[idx] && (
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                value={customDlvCompany[idx] || ''}
                                                                                onChange={(e) => onCustomDlvCompanyChange(idx, e.target.value)}
                                                                                placeholder="운송사명 입력"
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    <td className="px-2 py-2">
                                                                        <input
                                                                            className="form-control form-control-sm"
                                                                            value={getItemSummary(item)}
                                                                            placeholder="판매내역"
                                                                            readOnly={true}
                                                                            onClick={() => openRepairModal(idx)}
                                                                            style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                                                        />
                                                                    </td>
                                                                    {/* <td className="px-2 py-2">
                                                                        <div className="d-flex flex-column" style={{ gap: '4px' }}>
                                                                            <div className="input-group input-group-sm">
                                                                                <div className="input-group-prepend">
                                                                                    <span className="input-group-text" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>비용</span>
                                                                                </div>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    value={item.totalAmount || 0}
                                                                                    onChange={(e) => changeItem(idx, { totalAmount: Number(e.target.value) || 0 })}
                                                                                    style={{ fontSize: '0.875rem' }}
                                                                                />
                                                                            </div>
                                                                            <div className="input-group input-group-sm">
                                                                                <div className="input-group-prepend">
                                                                                    <span className="input-group-text" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>원가</span>
                                                                                </div>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    value={item.totalCost || 0}
                                                                                    onChange={(e) => changeItem(idx, { totalCost: Number(e.target.value) || 0 })}
                                                                                    style={{ fontSize: '0.875rem' }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </td> */}
                                                                    <td className="px-2 py-2">
                                                                        <div className="d-flex" style={{ gap: '4px' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-primary"
                                                                                onClick={() => openRepairModal(idx)}
                                                                                title="수리/판매목록"
                                                                            >
                                                                                <i className="fas fa-list"></i>
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-danger"
                                                                                onClick={() => removeItem(idx)}
                                                                                title="삭제"
                                                                            >
                                                                                <i className="fas fa-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        )))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                </div>

                                {/* 합계 푸터 - 반응형 */}
                                {hasFinancePermission &&
                                    <div className="card-footer">
                                        <div className="row">
                                            {isMobile ? (
                                                // 모바일용 레이아웃
                                                <div className="col-12">
                                                    <div className="bg-light p-2 rounded">
                                                        {/* 총 수리/판매 내역 */}
                                                        <div className="mb-3">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <small className="text-muted font-weight-bold">총 수리/판매내역</small>
                                                            </div>
                                                            <div
                                                                className="border rounded p-2 bg-white"
                                                                style={{
                                                                    fontSize: '0.85rem',
                                                                    minHeight: '50px',
                                                                    maxHeight: '80px',
                                                                    overflowY: 'auto',
                                                                    lineHeight: '1.4',
                                                                    wordBreak: 'keep-all'
                                                                }}
                                                            >
                                                                {totals.totalSummary || '내역 없음'}
                                                            </div>
                                                        </div>

                                                        {/* 금액 정보 - 2x3 그리드 */}
                                                        <div className="row mb-3">
                                                            <div className="col-6 mb-2">
                                                                <div className="text-center p-2 border rounded bg-white">
                                                                    <small className="text-muted d-block mb-1">청구금액</small>
                                                                    <div className="font-weight-bold text-primary" style={{ fontSize: '1.1rem' }}>
                                                                        {totals.estimateAmount.toLocaleString()}
                                                                        <small className="text-muted ml-1">원</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-6 mb-2">
                                                                <div className="text-center p-2 border rounded bg-white">
                                                                    <small className="text-muted d-block mb-1">수리원가</small>
                                                                    <div className="font-weight-bold text-info" style={{ fontSize: '1.1rem' }}>
                                                                        {totals.repairCost.toLocaleString()}
                                                                        <small className="text-muted ml-1">원</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-6 mb-2">
                                                                <div className="text-center p-2 border rounded bg-white">
                                                                    <small className="text-muted d-block mb-1">출장비(고객)</small>
                                                                    <div className="font-weight-bold text-primary" style={{ fontSize: '1.1rem' }}>
                                                                        {(form.tripFee || 0).toLocaleString()}
                                                                        <small className="text-muted ml-1">원</small>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="col-6 mb-2">
                                                                <div className="text-center p-2 border rounded bg-white">
                                                                    <small className="text-muted d-block mb-1">출장비용(출장자)</small>
                                                                    <div className="font-weight-bold text-danger" style={{ fontSize: '1.1rem' }}>
                                                                        {totals.tripFeeAmount.toLocaleString()}
                                                                        <small className="text-muted ml-1">원</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-12">
                                                                <div className="text-center p-2 border rounded" style={{
                                                                    backgroundColor: totals.profit >= 0 ? '#d4edda' : '#f8d7da'
                                                                }}>
                                                                    <small className="text-muted d-block mb-1">이익</small>
                                                                    <div
                                                                        className={`font-weight-bold ${totals.profit >= 0 ? 'text-success' : 'text-danger'}`}
                                                                        style={{ fontSize: '1.3rem' }}
                                                                    >
                                                                        {totals.profit.toLocaleString()}
                                                                        <small className="ml-1">원</small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* 추가 정보 입력 */}
                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                결제내역
                                                            </label>
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={form.paymentMethod || ''}
                                                                onChange={(e) => onChange('paymentMethod', e.target.value as PaymentMethod || null)}
                                                            >
                                                                <option value="">선택</option>
                                                                {Object.entries(PAYMENT_LABELS).map(([value, label]) => (
                                                                    <option key={value} value={value}>{label}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                증빙유형
                                                            </label>
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={form.proofType || ''}
                                                                onChange={(e) => onChange('proofType', e.target.value as ProofType || null)}
                                                            >
                                                                <option value="">선택</option>
                                                                {Object.entries(PROOF_LABELS).map(([value, label]) => (
                                                                    <option key={value} value={value}>{label}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                출장비용
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                value={form.tripFeeAmount || 0}
                                                                onChange={(e) => onChange('tripFeeAmount', Number(e.target.value) || 0)}
                                                                placeholder="출장비용을 입력하세요"
                                                            />
                                                        </div>

                                                        <div className="mb-2">
                                                            <label className="form-label mb-1" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                견적일자
                                                            </label>
                                                            <input
                                                                type="date"
                                                                className="form-control form-control-sm"
                                                                value={form.estimateDate || ''}
                                                                onChange={(e) => onChange('estimateDate', e.target.value)}
                                                            />
                                                        </div>

                                                        <div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-warning btn-sm w-100"
                                                                onClick={() => openEstimateModal()}
                                                            >
                                                                <i className="fas fa-file-invoice mr-1"></i>견적서 발행
                                                                {form?.estimateIssueDate && <span className="badge badge-success ml-1">작성됨</span>}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                // 데스크톱용 기존 레이아웃
                                                <>
                                                    <div className="col-md-12">
                                                        <div className="bg-light p-3 rounded">
                                                            {/* 금액 정보 */}
                                                            <div className="row text-center align-items-end">
                                                                <div className="col-2">
                                                                    <small className="text-muted">견적금액</small>
                                                                    <div className="font-weight-bold text-primary">
                                                                        {totals.estimateAmount.toLocaleString()}원
                                                                    </div>
                                                                </div>
                                                                <div className="col-4">
                                                                    <small className="text-muted d-block mb-1">총수리판매내역</small>
                                                                    <div
                                                                        className="font-weight-bold text-dark border rounded p-2 bg-white text-left"
                                                                        style={{
                                                                            fontSize: '0.85em',
                                                                            minHeight: '38px',
                                                                            maxHeight: '80px',
                                                                            overflowY: 'auto',
                                                                            lineHeight: '1.3',
                                                                            wordBreak: 'keep-all',
                                                                            overflowWrap: 'break-word'
                                                                        }}
                                                                    >
                                                                        {totals.totalSummary || '내역 없음'}
                                                                    </div>
                                                                </div>

                                                                <div className="col-2">
                                                                    <small className="text-muted">수리원가</small>
                                                                    <div className="font-weight-bold text-info">
                                                                        {totals.repairCost.toLocaleString()}원
                                                                    </div>
                                                                </div>

                                                                {/* 출장비 정보 - 2개 필드로 분리 */}
                                                                <div className="col-1">
                                                                    <small className="text-muted">출장비<br />(고객청구)</small>
                                                                    <div className="font-weight-bold text-primary">
                                                                        {(form.tripFee || 0).toLocaleString()}원
                                                                    </div>
                                                                </div>

                                                                <div className="col-1">
                                                                    <small className="text-muted">출장비용<br />(출장자)</small>
                                                                    <div className="font-weight-bold text-danger">
                                                                        {totals.tripFeeAmount.toLocaleString()}원
                                                                    </div>
                                                                </div>

                                                                <div className="col-1">
                                                                    <small className="text-muted">물류비</small>
                                                                    <div className="font-weight-bold text-info">
                                                                        {totals.logisticsFee.toLocaleString()}원
                                                                    </div>
                                                                </div>

                                                                <div className="col-2">
                                                                    <small className="text-muted">이익</small>
                                                                    <div className={`font-weight-bold ${totals.profit >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1.1em' }}>
                                                                        {totals.profit.toLocaleString()}원
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-12 mt-2">
                                                        <div className="row justify-content-end">
                                                            <div className="col-md-2">
                                                                <label className="form-label">결제내역</label>
                                                                <div className='form-control form-control-sm'>
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={form.paymentMethod || ''}
                                                                        onChange={(e) => onChange('paymentMethod', e.target.value as PaymentMethod || null)}
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">선택</option>
                                                                        {Object.entries(PAYMENT_LABELS).map(([value, label]) => (
                                                                            <option key={value} value={value}>{label}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-2">
                                                                <label className="form-label">증빙유형</label>
                                                                <div className='form-control form-control-sm'>
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={form.proofType || ''}
                                                                        onChange={(e) => onChange('proofType', e.target.value as ProofType || null)}
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <option value="">선택</option>
                                                                        {Object.entries(PROOF_LABELS).map(([value, label]) => (
                                                                            <option key={value} value={value}>{label}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            {/* 출장비(고객 청구) 입력 필드 추가 */}
                                                            <div className="col-md-2">
                                                                <label className="form-label">출장비(고객)</label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={form.tripFee || 0}
                                                                    onChange={(e) => onChange('tripFee', Number(e.target.value) || 0)}
                                                                    placeholder="출장비(고객 청구)"
                                                                    style={{ fontWeight: 'bold', color: '#007bff' }}
                                                                />
                                                                <small className="text-muted">도 기준 자동입력</small>
                                                            </div>

                                                            {/* 출장 비용(출장자) 입력 필드 - 기존 tripFeeAmount */}
                                                            <div className="col-md-2">
                                                                <label className="form-label">출장비용(출장자)</label>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={form.tripFeeAmount || 0}
                                                                    onChange={(e) => onChange('tripFeeAmount', Number(e.target.value) || 0)}
                                                                    placeholder="출장 비용(출장자 지불)"
                                                                    style={{ fontWeight: 'bold', color: '#dc3545' }}
                                                                />
                                                                <small className="text-muted">지역 기준 자동입력</small>
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label">견적일자</label>
                                                                <input
                                                                    type="date"
                                                                    className="form-control form-control-sm"
                                                                    value={form.estimateDate || ''}
                                                                    onChange={(e) => onChange('estimateDate', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="col-md-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-warning btn-sm"
                                                                    onClick={() => openEstimateModal()}
                                                                    style={{ marginTop: '1.5rem' }}
                                                                >
                                                                    <i className="fas fa-file-invoice mr-1"></i>견적서 발행
                                                                    {form?.estimateIssueDate && <span className="badge badge-success ml-1">작성됨</span>}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                }
                            </div>

                        </div>

                        {/* 푸터 */}
                        <div className="modal-footer d-flex justify-content-between align-items-center">
                            {/* 좌측: 택배 버튼 */}
                            <div className="d-flex" style={{ gap: '8px' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => openDlvModal('inbound')}
                                >
                                    택배입고
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => openDlvModal('outbound')}
                                >
                                    택배출고
                                </button>
                            </div>

                            {/* 우측: 기존 버튼들 */}
                            <div className="d-flex" style={{ gap: '8px' }}>
                                {form.id && (
                                    <button type="button" className="btn btn-danger" onClick={handleDelete}>
                                        삭제
                                    </button>
                                )}
                                <button type="button" className="btn btn-secondary" onClick={handleSaveAndNew}>
                                    저장 후 새접수
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm mr-1"></span>
                                            저장중...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save mr-1"></i>
                                            저장
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 수리내역 모달 - 개선된 버전 */}
            {showRepairModal && repairItemIndex !== null && (
                (() => {
                    const currentItem = form.items[repairItemIndex];
                    const modalConfig = getModalTitleAndProducts(currentItem?.kind || ReceptionItemKind.BOILER);

                    return (
                        <div
                            className="modal fade show"
                            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
                            tabIndex={-1}
                            role="dialog"
                        >
                            <div
                                className="modal-dialog modal-lg"
                                role="document"
                                style={{ maxHeight: '95vh', margin: '1rem auto', display: 'flex', alignItems: 'center' }}
                            >
                                <div
                                    className="modal-content"
                                    style={{ maxHeight: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column' }}
                                >
                                    {/* 헤더 - 고정 */}
                                    <div className="modal-header bg-warning text-dark" style={{ flexShrink: 0 }}>
                                        <h4 className="modal-title">
                                            <i className={`${modalConfig.iconClass} mr-2`}></i>
                                            {modalConfig.modalTitle} - {currentItem?.modelName || (currentItem?.kind === ReceptionItemKind.BOILER ? '보일러' : '판매품목')}
                                        </h4>
                                        <button type="button" className="close" onClick={closeRepairModal}>
                                            <span>&times;</span>
                                        </button>
                                    </div>

                                    {/* 바디 - 스크롤 영역 (일반, 대용량만) */}
                                    <div
                                        className="modal-body"
                                        style={{
                                            maxHeight: 'calc(100vh - 380px)', // 헤더, 푸터, 기타, 하단 고정 영역 제외
                                            overflowY: 'auto',
                                            flex: '0 1 auto'
                                        }}
                                    >
                                        {/* 수리내역(일반) */}
                                        <div className="mb-3">
                                            <h6 className="font-weight-bold mb-2">{modalConfig.sectionTitles.general}</h6>
                                            <div className="row">
                                                {modalConfig.generalProducts.map(product => (
                                                    <div key={product.id} className="col-md-4 col-lg-3 mb-2">
                                                        <div className="card card-body p-2" style={{ fontSize: '0.85rem' }}>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div className="flex-grow-1 pr-1">
                                                                    <div className="font-weight-bold" title={product.productName} style={{ fontSize: '0.75rem' }}>
                                                                        {product.productName}
                                                                    </div>
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    style={{ width: '50px', fontSize: '0.8rem' }}
                                                                    min="0"
                                                                    value={repairCounts[product.productName]?.quantity || 0}
                                                                    onChange={(e) => updateRepairCount(product.productName, Number(e.target.value) || 0, product)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* 수리내역(대용량) */}
                                        <div className="mb-3">
                                            <h6 className="font-weight-bold mb-2">{modalConfig.sectionTitles.heavy}</h6>
                                            <div className="row">
                                                {modalConfig.heavyProducts.map(product => (
                                                    <div key={product.id} className="col-md-4 col-lg-3 mb-2">
                                                        <div className="card card-body p-2" style={{ fontSize: '0.85rem' }}>
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div className="flex-grow-1 pr-1">
                                                                    <div className="font-weight-bold" title={product.productName} style={{ fontSize: '0.75rem' }}>
                                                                        {product.productName}
                                                                    </div>
                                                                </div>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    style={{ width: '50px', fontSize: '0.8rem' }}
                                                                    min="0"
                                                                    value={repairCounts[product.productName]?.quantity || 0}
                                                                    onChange={(e) => updateRepairCount(product.productName, Number(e.target.value) || 0, product)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 기타(무상) - 고정 영역 */}
                                    <div
                                        className="border-top px-3 py-2"
                                        style={{ flexShrink: 0, backgroundColor: '#f8f9fa' }}
                                    >
                                        <h6 className="font-weight-bold mb-2">{modalConfig.sectionTitles.etc}</h6>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="freeRepair"
                                                checked={repairCounts['무상']?.quantity > 0}
                                                onChange={(e) => updateRepairCount('무상', e.target.checked ? 1 : 0)}
                                            />
                                            <label className="form-check-label text-danger font-weight-bold" htmlFor="freeRepair">
                                                무상
                                            </label>
                                        </div>
                                    </div>

                                    {/* 판매내역(기타) - 고정 영역 */}
                                    <div
                                        className="border-top bg-light px-3 py-3"
                                        style={{ flexShrink: 0 }}
                                    >
                                        <div className="row">
                                            <div className="col-md-6">
                                                <label className="form-label">
                                                    {currentItem?.kind === 'BOILER' ? '수리내역' : '판매내역'}
                                                </label>
                                                <input
                                                    className="form-control form-control-sm"
                                                    value={Object.values(repairCounts)
                                                        .filter(item => item.quantity > 0)
                                                        .map(item => item.quantity === 1 ? item.productName : `${item.productName}×${item.quantity}`)
                                                        .join(', ')}
                                                    readOnly
                                                    placeholder="부품 수량에 따라 자동 생성"
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">
                                                    {currentItem?.kind === 'BOILER' ? '수리비용' : '판매비용'}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm bg-white"
                                                    value={calculateRepairCosts().totalPrice}
                                                    readOnly
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">원가</label>
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm bg-white"
                                                    value={calculateRepairCosts().totalCost}
                                                    readOnly
                                                    placeholder="0"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 푸터 - 고정 */}
                                    <div className="modal-footer" style={{ flexShrink: 0 }}>
                                        <button type="button" className="btn btn-secondary" onClick={closeRepairModal}>
                                            취소
                                        </button>
                                        <button type="button" className="btn btn-warning" onClick={saveRepairDetails}>
                                            <i className="fas fa-save mr-1"></i>
                                            저장
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()
            )}

            {/* 출장카드 모달 */}
            <TripCardModal
                show={showTripCardModal}
                title="출장카드"
                initial={form}
                onSave={saveTripCard}
                onClose={closeTripCardModal}
            />

            {/* 견적서 모달 */}
            <EstimateModal
                show={showEstimateModal}
                reception={form}
                onClose={(estimateItems, updatedReception) => closeEstimateModal(estimateItems, updatedReception)}
            />

            {/* DlvModal */}
            <DlvModal
                show={showDlvModal}
                type={dlvModalType}
                loading={loading}
                initial={form}
                onSave={handleDlvModalSave}
                onClose={closeDlvModal}
            />
        </>
    );
};

export default ReceptionModal;